import { useContext, useEffect, useState } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";
import Format from "../../../Utility/Format";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid2";
import MUIBox from "@mui/material/Box";
import MUITextField from "@mui/material/TextField";
import MUITypography from "@mui/material/Typography";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUISwitch from "@mui/material/Switch";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIHealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MUISaveIcon from "@mui/icons-material/Save";
import MUIAlert from "@mui/material/Alert";
import MUIAlertTitle from "@mui/material/AlertTitle";
import MUISkeleton from "@mui/material/Skeleton";
import MUIDivider from "@mui/material/Divider";
import MUIStack from "@mui/material/Stack";
import MUITooltip from "@mui/material/Tooltip";
import MUIRadioGroup from "@mui/material/RadioGroup";
import MUIRadio from "@mui/material/Radio";
import MUIFormControl from "@mui/material/FormControl";
import MUICheckCircleIcon from "@mui/icons-material/CheckCircle";
import MUIAutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MUICloseIcon from "@mui/icons-material/Close";
import MUIPolicyIcon from "@mui/icons-material/Policy";
import MUIRedoIcon from "@mui/icons-material/Redo";
import MUIAddIcon from "@mui/icons-material/Add";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIFormHelperText from "@mui/material/FormHelperText";
import MUIChip from "@mui/material/Chip";
import MUICollapse from "@mui/material/Collapse";
import MUIExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MUIPersonIcon from "@mui/icons-material/Person";

import Card from "../../Card";
import InfoItem from "../../InfoItem";
import InsuranceType from "../../../Utility/Crud/InsuranceType";
import InsuranceSubType from "../../../Utility/Crud/InsuranceSubType";
import Eligibility from "../../../Utility/Eligibility";
import api from "../../../Seating/Security/api";
import Patient from "../../../Utility/Crud/Patient";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import Menu from "../../Menu";
import Security from "../../../Utility/Security";

/**
 * CardChartInsurance renders a Card component
 * displaying patient or order insurance information.
 * @returns {JSX.Element}
 */

const title = "Insurance";
const icon = MUIHealthAndSafetyIcon;

const className = "CardChartInsurance";

export default function CardChartInsurance(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);

  const patient = chartContext.chart.patient[chartContext.patientId];
  const originalPatient =
    chartContext.originalChart.patient[chartContext.patientId];
  const order = chartContext.chart.orderActivity[chartContext.orderId];
  const originalOrder =
    chartContext.originalChart.orderActivity[chartContext.orderId];

  const [editMode, setEditMode] = useState(false);
  const [keepEditModeOpen, setKeepEditModeOpen] = useState(false);
  const [cardChart, setCardChart] = useState(null);
  const [
    primaryInsuranceApiResultLoading,
    setPrimaryInsuranceApiResultLoading,
  ] = useState(false);
  const [
    secondaryInsuranceApiResultLoading,
    setSecondaryInsuranceApiResultLoading,
  ] = useState(false);
  const [sameSimVerificationLoading, setSameSimVerificationLoading] =
    useState(false);
  const [snfVerificationLoading, setSNFVerificationLoading] = useState(false);
  const [
    transientPrimaryInsuranceApiResult,
    setTransientPrimaryInsuranceApiResult,
  ] = useState(null);
  const [
    transientSecondaryInsuranceApiResult,
    setTransientSecondaryInsuranceApiResult,
  ] = useState(null);
  const [primaryInsuranceDirty, setPrimaryInsuranceDirty] = useState(false);
  const [secondaryInsuranceDirty, setSecondaryInsuranceDirty] = useState(false);
  const [sameSimResults, setSameSimResults] = useState(null);
  const [snfResults, setSNFResults] = useState(null);

  const [insuranceInNetworkView, setInsuranceInNetworkView] = useState(true);
  const [secondaryInsuranceInNetworkView, setSecondaryInsuranceInNetworkView] =
    useState(true);

  const [hasSecondaryInsurance, setHasSecondaryInsurance] = useState(false);
  const [secondaryInsuranceData, setSecondaryInsuranceData] = useState({});

  const primaryInsuranceApiResults = Object.values(
    chartContext.chart?.apiResult || {},
  ).filter(
    (result) =>
      result.orderId === chartContext.orderId &&
      result.resultType.toLowerCase() === "primary",
  );
  const secondaryInsuranceApiResults = Object.values(
    chartContext.chart?.apiResult || {},
  ).filter(
    (result) =>
      result.orderId === chartContext.orderId &&
      result.resultType.toLowerCase() === "secondary",
  );
  const sameSimApiResults = Object.values(
    chartContext.chart?.apiResult || {},
  ).filter(
    (result) =>
      result.orderId === chartContext.orderId &&
      result.resultType.toLowerCase() === "samesim",
  );
  const snfApiResults = Object.values(
    chartContext.chart?.apiResult || {},
  ).filter(
    (result) =>
      result.orderId === chartContext.orderId &&
      result.resultType.toLowerCase() === "snf",
  );

  const [insuranceVerified, setInsuranceVerified] = useState(false);
  const [insuranceVerificationNoteText, setInsuranceVerificationNoteText] =
    useState("");
  const [newNoteId, setNewNoteId] = useState(null);
  const [addVerificationNote, setAddVerificationNote] = useState(false);
  const [subscriberInfoExpanded, setSubscriberInfoExpanded] = useState({
    primary: false,
    secondary: false,
  });

  const [overrideEligibilityRestrictions, setOverrideEligibilityRestrictions] =
    useState(false);

  const showVerifiedChip =
    props?.showVerifiedChip !== undefined ? props?.showVerifiedChip : true;
  const showCheckEligibilityButton =
    props?.showCheckEligibilityButton !== undefined
      ? props?.showCheckEligibilityButton
      : true;
  const showEligibilitySummary =
    props?.showEligibilitySummary !== undefined
      ? props?.showEligibilitySummary
      : true;
  const showCheckSameSimButton =
    props?.showCheckSameSimButton !== undefined
      ? props?.showCheckSameSimButton
      : true;
  const showCheckSNFButton =
    props?.showCheckSNFButton !== undefined ? props?.showCheckSNFButton : true;
  const showCreateVerificationNoteButton =
    props?.showCreateVerificationNoteButton !== undefined
      ? props?.showCreateVerificationNoteButton
      : true;
  const allowCreateNoteAlways =
    props?.allowCreateNoteAlways !== undefined
      ? props?.allowCreateNoteAlways
      : true;
  const allowDisablingFields =
    props?.allowDisablingFields !== undefined
      ? props?.allowDisablingFields
      : true;
  const showEditButton =
    props?.showEditButton !== undefined ? props?.showEditButton : true;

  const dataSource = chartContext.orderId ? order : patient;
  const openAndUnapprovedOrders = Object.values(
    chartContext.chart.orderActivity,
  ).filter(
    (order) =>
      order?.isApproved !== true && order.status !== 4 && order.status !== 3,
  ); // 3 = Setup, 4 = Cancelled

  const getInsuranceFieldPrefix = (insuranceType) =>
    insuranceType === "primary" ? "insurance" : "secondaryInsurance";
  const shouldSyncInsuranceChanges = () => {
    // Intentionally force sync for any orderActivity insurance edit while
    // preserving the legacy condition shape for easy rollback later.
    return true || order?.orderType !== "New" || dataSource === "patient";
  };

  const renderSubscriberInfoAlert = (eligibilityResults, insuranceType) => {
    const subscriberInfo =
      eligibilityResults?.detail?.subscriberInfo ||
      eligibilityResults?.detail?.noteData?.SubscriberInfo ||
      null;
    if (!subscriberInfo) {
      return null;
    }

    if (!(
      subscriberInfo.name ||
      subscriberInfo.address1 ||
      subscriberInfo.address2 ||
      subscriberInfo.city ||
      subscriberInfo.state ||
      subscriberInfo.zip ||
      subscriberInfo.dob ||
      subscriberInfo.gender
    )) {
      return null;
    }

    const expanded = Boolean(subscriberInfoExpanded[insuranceType]);
    const subscriberDob = Format.localDate(subscriberInfo.dob) || "N/A";
    const subscriberGender = subscriberInfo.gender || "N/A";
    const subscriberZip = (() => {
      if (!subscriberInfo.zip) {
        return "N/A";
      }

      const rawZip = String(subscriberInfo.zip).trim();
      const digitsOnly = rawZip.replace(/\D/g, "");

      if (digitsOnly.length === 9) {
        return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
      }

      return rawZip || "N/A";
    })();
    const addressParts = [
      subscriberInfo.address1,
      subscriberInfo.address2,
    ].filter(Boolean);
    const cityStateZip = [
      subscriberInfo.city,
      [subscriberInfo.state, subscriberZip].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");
    const subscriberAddress =
      addressParts.length === 0
        ? cityStateZip || "N/A"
        : [addressParts.join(", "), cityStateZip].filter(Boolean).join(", ");

    return (
      <MUIAlert
        severity="info"
        icon={<MUIPersonIcon fontSize="inherit" />}
        sx={{
          mt: 1,
          width: "100%",
          cursor: "pointer",
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
        onClick={() =>
          setSubscriberInfoExpanded((prev) => ({
            ...prev,
            [insuranceType]: !prev[insuranceType],
          }))
        }
      >
        <MUIStack spacing={1} sx={{ width: "100%" }}>
          <MUIStack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MUITypography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Subscriber: {subscriberInfo.name || "N/A"}
            </MUITypography>
            <MUIExpandMoreIcon
              fontSize="small"
              sx={{
                flexShrink: 0,
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 150ms ease",
              }}
            />
          </MUIStack>
          <MUICollapse in={expanded} timeout="auto" unmountOnExit>
            <MUIDivider sx={{ my: 1 }} />
            <MUITypography variant="body2">
              Address: {subscriberAddress}
            </MUITypography>
            <MUITypography variant="body2">DOB: {subscriberDob}</MUITypography>
            <MUITypography variant="body2">
              Gender: {subscriberGender}
            </MUITypography>
          </MUICollapse>
        </MUIStack>
      </MUIAlert>
    );
  };

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    if (!keepEditModeOpen) {
      setEditMode(false);
    }
    if (!keepEditModeOpen) {
      setOverrideEligibilityRestrictions(false);
    }
    setKeepEditModeOpen(false);
    setSecondaryInsuranceData({});
    setAddVerificationNote(false);
    setInsuranceVerificationNoteText("");
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  // Set secondary insurance switch state based on secondary insurance data
  useEffect(() => {
    if (
      dataSource?.secondaryInsuranceTypeId ||
      dataSource?.secondaryInsuranceSubTypeId
    ) {
      setHasSecondaryInsurance(true);
    } else {
      setHasSecondaryInsurance(false);
    }
  }, [chartContext.orderId]);

  // Set secondary insurance data so it can be restored when secondary insurance switch is checked
  useEffect(() => {
    setSecondaryInsuranceData({
      secondaryInsuranceTypeId: dataSource?.secondaryInsuranceTypeId,
      secondaryInsuranceSubTypeId: dataSource?.secondaryInsuranceSubTypeId,
      secondaryInsurancePolicy: dataSource?.secondaryInsurancePolicy,
      secondaryInsuranceGroup: dataSource?.secondaryInsuranceGroup,
    });
  }, [chartContext.orderId, chartContext.originalChart]);

  useEffect(() => {
    setSubscriberInfoExpanded({
      primary: false,
      secondary: false,
    });
  }, [chartContext.orderId]);

  /**
   * Creates a verification note for the order.
   */
  useEffect(() => {
    if (!showCreateVerificationNoteButton) {
      return;
    }

    const verifiedInsurances = getVerifiedActiveInsurances(true);
    if (addVerificationNote) {
      const createNoteBlocked = Boolean(getCreateNoteBlockingMessage());

      if (createNoteBlocked) {
        setInsuranceVerified(false);
        setInsuranceVerificationNoteText("");
        setNewNoteId(null);
        setCardChart(null);
        return;
      }

      setNewNoteId(chartContext.getNegativeId());
      setInsuranceVerified(false);
      chartContext.onChange({
        ["orderActivity"]: {
          [chartContext.orderId]: { insuranceVerified: null },
        },
      }); // Null requires unrestricted users to select yes or no.
      setCardChart({ ...cardChart, insuranceVerified: null });

      // If there are verified insurances, create boilerplate note text
      if (verifiedInsurances.length > 0) {
        let noteText = `${order.id} ${order.patientName}\nCoverage Summary\n`;
        let address = null;
        const sameSimResult = Eligibility.getSameSim(sameSimApiResults);
        if (sameSimResult && sameSimResult.status) {
          if (sameSimResult.status === "pending") {
            noteText += `\nSame/Sim: Pending`;
          } else if (sameSimResult.status === "error") {
            noteText += `\nSame/Sim: ${sameSimResult.detail?.errorMessage || "Error checking Same/Sim"}`;
          } else if (sameSimResult.status === "warning") {
            const claims = sameSimResult.detail?.claims || [];
            if (claims.length > 0) {
              const list = claims
                .map(
                  (c) =>
                    `${c.hcpcsCode} on ${moment(c.dosStart).format("MM/DD/YYYY")}`,
                )
                .join(", ");
              noteText += `\nSame/Sim: Wheelchair claim on file in last 5 years (${list})`;
            } else {
              noteText += `\nSame/Sim: Wheelchair claim on file in last 5 years`;
            }
          } else if (sameSimResult.status === "processed") {
            if (sameSimResult.eligible === false) {
              noteText += `\nSame/Sim: Same/Sim on file`;
            } else if (sameSimResult.eligible === true) {
              noteText += `\nSame/Sim: No Same/Sim on file`;
            }
          }

          noteText += `\n`;
        }

        // Add primary insurance summary if it exists
        const primaryInsurance = verifiedInsurances.find(
          (insurance) => insurance.type === "primary",
        );
        if (primaryInsurance?.results) {
          const primary = primaryInsurance.results;
          noteText += `\nPrimary: ${mainContext.insuranceTypesIndexed[order.insuranceTypeId]?.name} - ${mainContext.insuranceSubTypesIndexed[order.insuranceSubTypeId]?.name}`;
          noteText += `\nPlan: ${primary.detail.planName || "N/A"}`;

          if (primary.detail.isMedicare) {
            noteText += `\nDeductible Part B: ${
              primary.detail.deductible.partB !== null
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(primary.detail.deductible.partB)
                : "N/A"
            }`;
          } else {
            noteText += `\nDeductible: ${
              primary.detail.deductible.inNetwork !== null
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(primary.detail.deductible.inNetwork)
                : "N/A"
            }`;
          }

          noteText += `\nCoinsurance: ${primary.detail.dmeSummary?.coInsuranceInNetwork || "N/A"}`;

          // Add raw additionalInfo string if present
          if (primary.detail?.additionalInfo) {
            // Try to get the raw string version if possible
            let rawAdditionalInfo = "";
            if (primary.detail.noteData?.AdditionalInfo) {
              rawAdditionalInfo = primary.detail.noteData.AdditionalInfo;
            } else if (primary.detail.noteData?.AddtionalInfo) {
              rawAdditionalInfo = primary.detail.noteData.AddtionalInfo;
            }
            if (rawAdditionalInfo && typeof rawAdditionalInfo === "string") {
              noteText += `\nAdditional Info:${rawAdditionalInfo}\n`;
            }
          }

          if (primary.detail?.exceptionNotes) {
            noteText += `\nException Notes: ${primary.detail.exceptionNotes}\n`;
          }
        }

        // Add secondary insurance summary if it exists
        const secondaryInsurance = verifiedInsurances.find(
          (insurance) => insurance.type === "secondary",
        );
        if (secondaryInsurance?.results) {
          const secondary = secondaryInsurance.results;
          noteText += `\nSecondary: ${mainContext.insuranceTypesIndexed[order.secondaryInsuranceTypeId]?.name} - ${mainContext.insuranceSubTypesIndexed[order.secondaryInsuranceSubTypeId]?.name}`;
          noteText += `\nPlan: ${secondary.detail.planName || "N/A"}`;

          if (secondary.detail.isMedicare) {
            noteText += `\nDeductible Part B: ${
              secondary.detail.deductible.partB !== null
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(secondary.detail.deductible.partB)
                : "N/A"
            }`;
          } else {
            noteText += `\nDeductible: ${
              secondary.detail.deductible.inNetwork !== null
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(secondary.detail.deductible.inNetwork)
                : "N/A"
            }`;
          }

          noteText += `\nCoinsurance: ${secondary.detail.dmeSummary?.coInsuranceInNetwork || "N/A"}`;

          // Add raw additionalInfo string if present
          if (secondary.detail?.additionalInfo) {
            let rawAdditionalInfo = "";
            if (secondary.detail.noteData?.AdditionalInfo) {
              rawAdditionalInfo = secondary.detail.noteData.AdditionalInfo;
            } else if (secondary.detail.noteData?.AddtionalInfo) {
              rawAdditionalInfo = secondary.detail.noteData.AddtionalInfo;
            }
            if (rawAdditionalInfo && typeof rawAdditionalInfo === "string") {
              noteText += `\nAdditional Info:${rawAdditionalInfo}\n`;
            }
          }
          if (secondary.detail?.exceptionNotes) {
            noteText += `\nException Notes: ${secondary.detail.exceptionNotes}\n`;
          }
        }

        // if (eligibilitySummaryText) {
        //     noteText += `\n${eligibilitySummaryText}\n`;
        // }

        // Add detailed note data for primary insurance
        if (primaryInsurance?.results?.detail?.noteData) {
          const p = primaryInsurance.results.detail?.noteData;
          if (
            p.DemographicInfo &&
            p.DemographicInfo.Subscriber &&
            p.DemographicInfo.Subscriber.Address1
          ) {
            noteText +=
              "\nPrimary Member ID: " +
              (p.DemographicInfo.Subscriber.Identification &&
              p.DemographicInfo.Subscriber.Identification.length > 0
                ? p.DemographicInfo.Subscriber.Identification.filter(
                    (x) => x.Type === "Member ID",
                  )[0].Code
                : order.insurancePolicy);
            address =
              "\n" +
              (p.DemographicInfo.Subscriber.Address1 || "") +
              "\n" +
              (p.DemographicInfo.Subscriber.Address2 || "") +
              "\n" +
              (p.DemographicInfo.Subscriber.City || "") +
              ", " +
              (p.DemographicInfo.Subscriber.State || "") +
              " " +
              (p.DemographicInfo.Subscriber.Zip || "");
          } else {
            noteText += "\nMember ID: " + order.insurancePolicy;
            address =
              "\n" +
              order.address +
              "\n" +
              order.city +
              ", " +
              order.state +
              " " +
              order.zipCode;
          }

          //if (currentCoverage) {
          if (p.PlanCoverageSummary) {
            [p.PlanCoverageSummary].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (v[oKey] !== null) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey],
                  });
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return "";
            });
          }

          if (p.OtherPayerInfo) {
            [p.OtherPayerInfo].map((d) => {
              var res = [];
              for (var pkey in d) {
                if (d[pkey] !== null && d[pkey] !== "None") {
                  res.push({
                    name: pkey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: d[pkey],
                  });
                }
              }
              if (res && res.length > 0) {
                return res.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return null;
            });
          }

          if (p.MedicareInfoSummary) {
            [p.MedicareInfoSummary].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (
                  oKey.toLowerCase().indexOf("part_a") === -1 &&
                  oKey.toLowerCase().indexOf("pt_slp_") === -1 &&
                  oKey.toLowerCase().indexOf("ot_capamount") === -1
                ) {
                  if (
                    v[oKey] &&
                    typeof v[oKey] === "object" &&
                    v[oKey].hasOwnProperty("Value")
                  ) {
                    ll.push({
                      name: oKey
                        .replace(/[_]/g, " ")
                        .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                      value: v[oKey].Value,
                      note: v[oKey].Notes,
                    });
                  } else if (
                    v[oKey] !== null &&
                    v[oKey] !== "None" &&
                    oKey.toLowerCase().indexOf("pharmacy") < 0 &&
                    !["bin", "pcn"].includes(oKey.toLowerCase())
                  ) {
                    ll.push({
                      name: oKey
                        .replace(/[_]/g, " ")
                        .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                      value: v[oKey],
                      note: null,
                    });
                  }
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return "";
            });
          }

          if (p.PCPAuthInfoSummary) {
            [p.PCPAuthInfoSummary].map((d) => {
              var res = [];
              for (var pkey in d) {
                if (d[pkey] !== null && d[pkey] !== "None") {
                  res.push({
                    name: pkey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: d[pkey],
                  });
                }
              }
              if (res && res.length > 0) {
                return res.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return null;
            });
          }

          if (p.MiscellaneousInfoSummary) {
            [p.MiscellaneousInfoSummary].map((d) => {
              var res = [];
              for (var pkey in d) {
                if (d[pkey] !== null) {
                  res.push({
                    name: pkey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: d[pkey],
                  });
                }
              }
              if (res && res.length > 0) {
                return res.map((r) => {
                  return (noteText += "\n" + r.name + ": " + r.value);
                });
              }
              return "";
            });
          }

          if (p.PlanBenefits === null && p.HBPC_Deductible_OOP_Summary) {
            [p.HBPC_Deductible_OOP_Summary].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (
                  v[oKey] &&
                  typeof v[oKey] === "object" &&
                  v[oKey].hasOwnProperty("Value")
                ) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey].Value,
                  });
                } else if (v[oKey] !== null) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey],
                  });
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return "";
            });
          }

          const isMedicaid = primaryInsurance?.results?.detail?.isMedicaid;
          if (!isMedicaid && p.PlanBenefits) {
            [p.PlanBenefits].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (
                  v[oKey] &&
                  typeof v[oKey] === "object" &&
                  v[oKey].hasOwnProperty("Value")
                ) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey].Value,
                  });
                } else if (v[oKey] !== null) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey],
                  });
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  if (l.value.includes("%")) {
                    return (noteText += "\n" + l.name + ": " + l.value);
                  }
                  return (noteText += "\n" + l.name + ": " + `$${l.value}`);
                });
              }
              return "";
            });
          }

          if (p?.DMESummary) {
            [p.DMESummary].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (
                  v[oKey] &&
                  typeof v[oKey] === "object" &&
                  v[oKey].hasOwnProperty("Value")
                ) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey].Value,
                    note: v[oKey].Notes,
                  });
                } else if (v[oKey] !== null && v[oKey] !== "None") {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey],
                    note: null,
                  });
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return "";
            });
          }

          noteText += "\n";
        }

        // Add detailed note data for secondary insurance
        if (secondaryInsurance?.results?.detail?.noteData) {
          const p = secondaryInsurance.results.detail?.noteData;
          if (
            p.DemographicInfo &&
            p.DemographicInfo.Subscriber &&
            p.DemographicInfo.Subscriber.Address1
          ) {
            noteText +=
              "\nSecondary Member ID: " +
              (p.DemographicInfo.Subscriber.Identification &&
              p.DemographicInfo.Subscriber.Identification.length > 0
                ? p.DemographicInfo.Subscriber.Identification.filter(
                    (x) => x.Type === "Member ID",
                  )[0].Code
                : order.insurancePolicy);
            address =
              "\n" +
              (p.DemographicInfo.Subscriber.Address1 || "") +
              "\n" +
              (p.DemographicInfo.Subscriber.Address2 || "") +
              "\n" +
              (p.DemographicInfo.Subscriber.City || "") +
              ", " +
              (p.DemographicInfo.Subscriber.State || "") +
              " " +
              (p.DemographicInfo.Subscriber.Zip || "");
          } else {
            noteText += "\nMember ID: " + order.insurancePolicy;
            address =
              "\n" +
              order.address +
              "\n" +
              order.city +
              ", " +
              order.state +
              " " +
              order.zipCode;
          }

          //if (currentCoverage) {
          if (p.PlanCoverageSummary) {
            [p.PlanCoverageSummary].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (v[oKey] !== null) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey],
                  });
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return "";
            });
          }

          if (p.OtherPayerInfo) {
            [p.OtherPayerInfo].map((d) => {
              var res = [];
              for (var pkey in d) {
                if (d[pkey] !== null && d[pkey] !== "None") {
                  res.push({
                    name: pkey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: d[pkey],
                  });
                }
              }
              if (res && res.length > 0) {
                return res.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return null;
            });
          }

          if (p.MedicareInfoSummary) {
            [p.MedicareInfoSummary].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (
                  oKey.toLowerCase().indexOf("part_a") === -1 &&
                  oKey.toLowerCase().indexOf("pt_slp_") === -1 &&
                  oKey.toLowerCase().indexOf("ot_capamount") === -1
                ) {
                  if (
                    v[oKey] &&
                    typeof v[oKey] === "object" &&
                    v[oKey].hasOwnProperty("Value")
                  ) {
                    ll.push({
                      name: oKey
                        .replace(/[_]/g, " ")
                        .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                      value: v[oKey].Value,
                      note: v[oKey].Notes,
                    });
                  } else if (
                    v[oKey] !== null &&
                    v[oKey] !== "None" &&
                    oKey.toLowerCase().indexOf("pharmacy") < 0 &&
                    !["bin", "pcn"].includes(oKey.toLowerCase())
                  ) {
                    ll.push({
                      name: oKey
                        .replace(/[_]/g, " ")
                        .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                      value: v[oKey],
                      note: null,
                    });
                  }
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return "";
            });
          }

          if (p.PCPAuthInfoSummary) {
            [p.PCPAuthInfoSummary].map((d) => {
              var res = [];
              for (var pkey in d) {
                if (d[pkey] !== null && d[pkey] !== "None") {
                  res.push({
                    name: pkey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: d[pkey],
                  });
                }
              }
              if (res && res.length > 0) {
                return res.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return null;
            });
          }

          if (p.MiscellaneousInfoSummary) {
            [p.MiscellaneousInfoSummary].map((d) => {
              var res = [];
              for (var pkey in d) {
                if (d[pkey] !== null) {
                  res.push({
                    name: pkey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: d[pkey],
                  });
                }
              }
              if (res && res.length > 0) {
                return res.map((r) => {
                  return (noteText += "\n" + r.name + ": " + r.value);
                });
              }
              return "";
            });
          }

          if (p.PlanBenefits === null && p.HBPC_Deductible_OOP_Summary) {
            [p.HBPC_Deductible_OOP_Summary].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (
                  v[oKey] &&
                  typeof v[oKey] === "object" &&
                  v[oKey].hasOwnProperty("Value")
                ) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey].Value,
                  });
                } else if (v[oKey] !== null) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey],
                  });
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return "";
            });
          }

          const isMedicaid = secondaryInsurance?.results?.detail?.isMedicaid;
          if (!isMedicaid && p.PlanBenefits) {
            [p.PlanBenefits].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (
                  v[oKey] &&
                  typeof v[oKey] === "object" &&
                  v[oKey].hasOwnProperty("Value")
                ) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey].Value,
                  });
                } else if (v[oKey] !== null) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey],
                  });
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  if (l.value.includes("%")) {
                    return (noteText += "\n" + l.name + ": " + l.value);
                  }
                  return (noteText += "\n" + l.name + ": " + `$${l.value}`);
                });
              }
              return "";
            });
          }

          if (p.DMESummary) {
            [p.DMESummary].map((v) => {
              var ll = [];
              for (var oKey in v) {
                if (
                  v[oKey] &&
                  typeof v[oKey] === "object" &&
                  v[oKey].hasOwnProperty("Value")
                ) {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey].Value,
                    note: v[oKey].Notes,
                  });
                } else if (v[oKey] !== null && v[oKey] !== "None") {
                  ll.push({
                    name: oKey
                      .replace(/[_]/g, " ")
                      .replace(/([a-zA-Z])([A-Z])([a-z])/g, "$1 $2$3"),
                    value: v[oKey],
                    note: null,
                  });
                }
              }
              if (ll && ll.length > 0) {
                return ll.map((l) => {
                  return (noteText += "\n" + l.name + ": " + l.value);
                });
              }
              return "";
            });
          }

          noteText += "\n";
        }

        setInsuranceVerificationNoteText(noteText);
      }
    } else {
      setInsuranceVerificationNoteText("");
      delete chartContext.chart.note[newNoteId];
      chartContext.onChange({ note: { ...chartContext.chart.note } });
    }
  }, [addVerificationNote]);

  /**
   * Creates a verification note for the order.
   */
  useEffect(() => {
    if (insuranceVerificationNoteText?.trim() !== "") {
      const note = {
        [newNoteId]: {
          id: newNoteId,
          accountId: OrderActivity.getAccount(order, chartContext.chart)?.id,
          activityId: chartContext.orderId,
          assignedToId: null,
          completed: true,
          createdById: mainContext.currentUser.id, // TODO Michael update note creation to not require this and throw it away if provided
          createdAt: new Date().toISOString(),
          dueDate: null,
          isAutoVerification: true,
          patientName: Patient.getDisplayName(patient),
          task: false,
          text: insuranceVerificationNoteText,
          type: "ActivityNote",
        },
      };
      chartContext.onChange({
        note: note,
      });
    }
    if (insuranceVerificationNoteText?.trim() !== "" && !insuranceVerified) {
      delete chartContext.chart.note[newNoteId];
      chartContext.onChange({ note: { ...chartContext.chart.note } });
    }
  }, [insuranceVerificationNoteText, insuranceVerified]);

  // Validation rules
  useEffect(() => {
    if (editMode) {
      const rules = [];
      if (!chartContext.orderId) {
        rules.push({
          target: "patient",
          id: patient?.id,
          field: "mbi",
          rule: { type: "mbi" },
        });
      }

      if (chartContext.orderId) {
        rules.push({
          target: "orderActivity",
          id: chartContext.orderId,
          field: "mbi",
          rule: { type: "mbi" },
        });
      }

      chartContext.updateValidationRules({
        key: className,
        rules: rules,
      });
    }
    if (addVerificationNote) {
      const rules = [];
      const createNoteBlocked = Boolean(getCreateNoteBlockingMessage());

      if (!createNoteBlocked) {
        rules.push({
          target: "orderActivity",
          id: chartContext.orderId,
          field: "insuranceVerified",
          rule: { required: true },
        });
      }

      chartContext.updateValidationRules({
        key: className,
        rules: rules,
      });
    } else {
      chartContext.updateValidationRules({ key: className, rules: [] });
    }
  }, [editMode, order, addVerificationNote]);

  /**
   * Returns the styles for a container that should be full width at a given max width.
   * Only used when sidebar=true to handle responsive container queries.
   * @param {number} maxWidth - The max width at which the container should be full width.
   * @returns {Object} The styles for the container.
   */
  const getContainerQuery = (maxWidth) => {
    if (!props.sidebar) {
      return {};
    }

    return {
      [`@container (max-width: ${maxWidth}px)`]: {
        flexBasis: "100% !important",
        maxWidth: "100% !important",
      },
    };
  };

  const handleInsuranceFieldChange = (fieldOrFields, value) => {
    const updates = {};

    // Define all insurance fields to copy
    const insuranceFields = [
      "insuranceTypeId",
      "insuranceSubTypeId",
      "insurancePolicy",
      "insuranceGroup",
      "secondaryInsuranceTypeId",
      "secondaryInsuranceSubTypeId",
      "secondaryInsurancePolicy",
      "secondaryInsuranceGroup",
      "mbi",
    ];

    // Build the update object with the changed field(s) and all existing insurance data
    const buildInsuranceUpdate = () => {
      const update = {};

      // Handle multiple fields passed as an object or single field with value
      if (typeof fieldOrFields === "object" && !Array.isArray(fieldOrFields)) {
        // Multiple fields passed as object: { field1: value1, field2: value2 }
        Object.assign(update, fieldOrFields);
      } else {
        // Single field passed
        update[fieldOrFields] = value;
      }

      // Copy all other insurance fields from dataSource
      insuranceFields.forEach((insuranceField) => {
        if (!(insuranceField in update)) {
          update[insuranceField] = dataSource?.[insuranceField] || null;
        }
      });

      return update;
    };

    // Update all open and unapproved orders
    openAndUnapprovedOrders.forEach((order) => {
      if (!updates.orderActivity) {
        updates.orderActivity = {};
      }
      updates.orderActivity[order.id] = buildInsuranceUpdate();
    });

    // The order being edited may already be Setup (for example, Unable to Bill - Equipment),
    // so it is not necessarily included in openAndUnapprovedOrders.
    if (
      chartContext.orderId &&
      !updates.orderActivity?.[chartContext.orderId]
    ) {
      updates.orderActivity = {
        ...updates.orderActivity,
        [chartContext.orderId]: buildInsuranceUpdate(),
      };
    }

    // Update patient
    updates.patient = {
      [chartContext.patientId]: buildInsuranceUpdate(),
    };

    chartContext.onChange(updates);
  };

  /**
   * Renders the edit view displaying patient insurance information.
   * @returns {JSX.Element} Grid layout with patient insurance information display
   */
  const renderEditInsuranceSection = (insuranceType) => {
    const insuranceFieldPrefix = getInsuranceFieldPrefix(insuranceType);
    const getInsuranceTypeOptions = () => {
      return InsuranceType.sort(
        Object.values(mainContext.insuranceTypesIndexed),
      ).map((option) => ({
        value: option.id,
        title: option.name,
      }));
    };

    const getInsuranceSubTypeOptions = () => {
      return InsuranceSubType.sort(
        Object.values(mainContext.insuranceSubTypesIndexed),
      )
        .filter(
          (subType) =>
            subType?.insuranceTypeId ===
            dataSource?.[`${insuranceFieldPrefix}TypeId`],
        )
        .map((option, index) => ({
          value: option?.id,
          title: option?.name,
          key: index, // Use ID as key since it should be unique and we have multiple dublicate products
        }));
    };

    const selectedInsuranceType =
      getInsuranceTypeOptions().find(
        (option) =>
          option.value === dataSource?.[`${insuranceFieldPrefix}TypeId`],
      ) || null;
    const selectedInsuranceSubType =
      getInsuranceSubTypeOptions().find(
        (option) =>
          option.value === dataSource?.[`${insuranceFieldPrefix}SubTypeId`],
      ) || null;

    return (
      <MUIGrid
        container
        spacing={props.sidebar ? 2 : 4}
        rowSpacing={2}
        sx={{ mt: 1 }}
      >
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 3,
            md: 6,
            xs: 12,
          }}
        >
          <MUIAutocomplete
            key={`${insuranceType}-type-autocomplete`}
            options={getInsuranceTypeOptions()}
            value={selectedInsuranceType ? selectedInsuranceType : null}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            getOptionLabel={(option) => option.title}
            disableClearable={insuranceType === "primary"}
            disabled={
              chartContext.orderId &&
              order?.isApproved === true &&
              allowDisablingFields
            }
            onChange={(e, newValue) => {
              if (insuranceType === "primary") {
                setPrimaryInsuranceDirty(true);
              } else {
                setSecondaryInsuranceDirty(true);
              }
              const updates = {};
              // Calculate the new subtype value
              const newSubTypeId = (() => {
                if (selectedInsuranceType?.value !== newValue?.value) {
                  const subTypeOptions = InsuranceSubType.sort(
                    Object.values(mainContext.insuranceSubTypesIndexed),
                  ).filter(
                    (subType) => subType.insuranceTypeId === newValue?.value,
                  );
                  return subTypeOptions.length === 1
                    ? subTypeOptions[0].id
                    : null;
                }
                return dataSource[`${insuranceFieldPrefix}SubTypeId`];
              })();

              if (shouldSyncInsuranceChanges()) {
                // Update all open and unapproved orders
                handleInsuranceFieldChange({
                  [`${insuranceFieldPrefix}TypeId`]: newValue
                    ? newValue.value
                    : null,
                  [`${insuranceFieldPrefix}SubTypeId`]: newSubTypeId,
                });
              } else {
                // Update only the current order
                updates.orderActivity = {
                  [chartContext.orderId]: {
                    [`${insuranceFieldPrefix}TypeId`]: newValue
                      ? newValue.value
                      : null,
                    [`${insuranceFieldPrefix}SubTypeId`]: newSubTypeId,
                  },
                };
              }
              chartContext.onChange(updates);
              setCardChart({
                ...cardChart,
                [`${insuranceFieldPrefix}TypeId`]: newValue
                  ? newValue.value
                  : null,
                [`${insuranceFieldPrefix}SubTypeId`]: newSubTypeId,
              });
            }}
            renderInput={(params) => (
              <MUITextField
                {...params}
                size="small"
                variant="standard"
                label="Payor Source"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            lg: 3,
            md: 6,
            xs: 12,
          }}
        >
          <MUIAutocomplete
            key={`${insuranceType}-subtype-autocomplete`}
            options={getInsuranceSubTypeOptions()}
            value={selectedInsuranceSubType ? selectedInsuranceSubType : null}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            getOptionLabel={(option) => option.title}
            disableClearable={insuranceType === "primary"}
            disabled={
              chartContext.orderId &&
              order?.isApproved === true &&
              allowDisablingFields
            }
            getOptionKey={(option) => option.key} // Must use this when we have multiple dublicate products to prevent console warnings
            onChange={(e, newValue) => {
              if (insuranceType === "primary") {
                setPrimaryInsuranceDirty(true);
              } else {
                setSecondaryInsuranceDirty(true);
              }
              const updates = {};
              if (shouldSyncInsuranceChanges()) {
                handleInsuranceFieldChange(
                  `${insuranceFieldPrefix}SubTypeId`,
                  newValue ? newValue.value : null,
                );
              } else {
                // Update only the current order
                updates.orderActivity = {
                  [chartContext.orderId]: {
                    [`${insuranceFieldPrefix}SubTypeId`]: newValue
                      ? newValue.value
                      : null,
                  },
                };
              }
              chartContext.onChange(updates);
              setCardChart({
                ...cardChart,
                [`${insuranceFieldPrefix}SubTypeId`]: newValue
                  ? newValue.value
                  : null,
              });
            }}
            renderInput={(params) => (
              <MUITextField
                {...params}
                size="small"
                variant="standard"
                label="Insurance"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </MUIGrid>
        {dataSource?.[`${insuranceFieldPrefix}TypeId`] !== 4 && (
          <>
            <MUIGrid
              sx={getContainerQuery(600)}
              size={{
                lg: 3,
                md: 6,
                xs: 12,
              }}
            >
              <MUITextField
                value={dataSource?.[`${insuranceFieldPrefix}Policy`] || ""}
                fullWidth
                label={
                  dataSource?.[`${insuranceFieldPrefix}TypeId`] === 1
                    ? "MBI"
                    : "Policy #"
                }
                disabled={
                  chartContext.orderId &&
                  order?.isApproved === true &&
                  allowDisablingFields
                }
                onChange={(e) => {
                  if (insuranceType === "primary") {
                    setPrimaryInsuranceDirty(true);
                  } else {
                    setSecondaryInsuranceDirty(true);
                  }
                  let value = e.target.value;
                  if (dataSource?.[`${insuranceFieldPrefix}TypeId`] === 1) {
                    // If MBI field, only allow letters and numbers
                    value = value.replace(/[^a-zA-Z0-9]/g, "");
                    // Save to both policy and mbi fields for Medicare
                    const updates = {};
                    if (shouldSyncInsuranceChanges()) {
                      handleInsuranceFieldChange({
                        [`${insuranceFieldPrefix}Policy`]: value.trim() || null,
                        mbi: value.trim() || null,
                      });
                    } else {
                      // Update only the current order
                      updates.orderActivity = {
                        [chartContext.orderId]: {
                          [`${insuranceFieldPrefix}Policy`]: value.trim(),
                          mbi: value.trim(),
                        },
                      };
                    }
                    chartContext.onChange(updates);
                    setCardChart({
                      ...cardChart,
                      [`${insuranceFieldPrefix}Policy`]: value.trim(),
                      mbi: value.trim(),
                    });
                  } else {
                    const updates = {};
                    if (shouldSyncInsuranceChanges()) {
                      handleInsuranceFieldChange(
                        `${insuranceFieldPrefix}Policy`,
                        value.trim() || null,
                      );
                    } else {
                      // Update only the current order
                      updates.orderActivity = {
                        [chartContext.orderId]: {
                          [`${insuranceFieldPrefix}Policy`]: value.trim(),
                        },
                      };
                    }
                    chartContext.onChange(updates);
                    setCardChart({
                      ...cardChart,
                      [`${insuranceFieldPrefix}Policy`]: value.trim(),
                    });
                  }
                }}
                variant="standard"
                error={
                  dataSource?.[`${insuranceFieldPrefix}TypeId`] === 1 &&
                  chartContext.validationIssues[className]?.[
                    chartContext?.orderId ? "orderActivity" : "patient"
                  ]?.[dataSource.id]?.mbi
                    ? true
                    : false
                }
                helperText={
                  dataSource?.[`${insuranceFieldPrefix}TypeId`] === 1 &&
                  chartContext.validationIssues[className]?.[
                    chartContext?.orderId ? "orderActivity" : "patient"
                  ]?.[dataSource.id]?.mbi
                    ? chartContext.validationIssues[className]?.[
                        chartContext?.orderId ? "orderActivity" : "patient"
                      ]?.[dataSource.id]?.mbi
                    : ""
                }
                slotProps={{
                  htmlInput: {
                    maxLength:
                      dataSource?.[`${insuranceFieldPrefix}TypeId`] === 1
                        ? 11
                        : undefined,
                  },

                  inputLabel: { shrink: true },
                }}
              />
            </MUIGrid>
            <MUIGrid
              sx={getContainerQuery(600)}
              size={{
                lg: 3,
                md: 6,
                xs: 12,
              }}
            >
              <MUITextField
                value={dataSource?.[`${insuranceFieldPrefix}Group`] || ""}
                fullWidth
                disabled={
                  chartContext.orderId &&
                  order?.isApproved === true &&
                  allowDisablingFields
                }
                label="Group #"
                onChange={(e) => {
                  const updates = {};
                  if (shouldSyncInsuranceChanges()) {
                    handleInsuranceFieldChange(
                      `${insuranceFieldPrefix}Group`,
                      e.target.value.trim(),
                    );
                  } else {
                    // Update only the current order
                    updates.orderActivity = {
                      [chartContext.orderId]: {
                        [`${insuranceFieldPrefix}Group`]: e.target.value.trim(),
                      },
                    };
                  }
                  chartContext.onChange(updates);
                  setCardChart({
                    ...cardChart,
                    [`${insuranceFieldPrefix}Group`]: e.target.value.trim(),
                  });
                }}
                variant="standard"
                slotProps={{
                  htmlInput: { style: { resize: "both" } },
                  inputLabel: { shrink: true },
                }}
              />
            </MUIGrid>
            {insuranceType === "primary" &&
              dataSource?.insuranceTypeId !== 1 &&
              dataSource?.secondaryInsuranceTypeId !== 1 && (
                <MUIGrid
                  sx={getContainerQuery(600)}
                  size={{
                    lg: 3,
                    md: 6,
                    xs: 12,
                  }}
                >
                  <MUITextField
                    value={dataSource?.mbi || ""}
                    fullWidth
                    label="MBI"
                    disabled={
                      chartContext.orderId &&
                      order?.isApproved === true &&
                      allowDisablingFields
                    }
                    onChange={(e) => {
                      if (insuranceType === "primary") {
                        setPrimaryInsuranceDirty(true);
                      } else {
                        setSecondaryInsuranceDirty(true);
                      }
                      let value = e.target.value;
                      // Only allow letters and numbers
                      value = value.replace(/[^a-zA-Z0-9]/g, "");

                      const updates = {};
                      if (shouldSyncInsuranceChanges()) {
                        handleInsuranceFieldChange("mbi", value.trim() || null);
                      } else {
                        // Update only the current order
                        updates.orderActivity = {
                          [chartContext.orderId]: {
                            mbi: value.trim(),
                          },
                        };
                      }
                      chartContext.onChange(updates);
                      setCardChart({
                        ...cardChart,
                        mbi: value.trim(),
                      });
                    }}
                    variant="standard"
                    error={
                      chartContext.validationIssues[className]?.[
                        chartContext?.orderId ? "orderActivity" : "patient"
                      ]?.[dataSource.id]?.mbi
                        ? true
                        : false
                    }
                    helperText={
                      chartContext.validationIssues[className]?.[
                        chartContext?.orderId ? "orderActivity" : "patient"
                      ]?.[dataSource.id]?.mbi
                        ? chartContext.validationIssues[className]?.[
                            chartContext?.orderId ? "orderActivity" : "patient"
                          ]?.[dataSource.id]?.mbi
                        : ""
                    }
                    slotProps={{
                      htmlInput: {
                        maxLength: 11,
                      },

                      inputLabel: { shrink: true },
                    }}
                  />
                </MUIGrid>
              )}
          </>
        )}
      </MUIGrid>
    );
  };

  /**
   * Renders the edit view displaying patient insurance information.
   * @returns {JSX.Element} Grid layout with patient insurance information display
   */
  const renderEditView = () => {
    const handleSecondaryInsuranceCheckboxChange = (e) => {
      setHasSecondaryInsurance(e.target.checked);
      if (!e.target.checked) {
        // Clear all secondary insurance fields if unchecked
        const updates = {};
        if (shouldSyncInsuranceChanges()) {
          handleInsuranceFieldChange({
            secondaryInsuranceTypeId: null,
            secondaryInsuranceSubTypeId: null,
            secondaryInsurancePolicy: null,
            secondaryInsuranceGroup: null,
          });
        } else {
          // Update only the current order
          updates.orderActivity = {
            [chartContext.orderId]: {
              secondaryInsuranceTypeId: null,
              secondaryInsuranceSubTypeId: null,
              secondaryInsurancePolicy: null,
              secondaryInsuranceGroup: null,
            },
          };
        }
        chartContext.onChange(updates);
        setCardChart({
          ...cardChart,
          secondaryInsuranceTypeId: null,
          secondaryInsuranceSubTypeId: null,
          secondaryInsurancePolicy: null,
          secondaryInsuranceGroup: null,
        });
      } else if (secondaryInsuranceData) {
        // Restore saved secondary insurance data if checked
        const updates = {};
        if (shouldSyncInsuranceChanges()) {
          handleInsuranceFieldChange(secondaryInsuranceData);
        } else {
          // Update only the current order
          updates.orderActivity = {
            [chartContext.orderId]: {
              ...secondaryInsuranceData,
            },
          };
        }
        chartContext.onChange(updates);
      }
    };

    const getInsuranceSubTypeOptions = (insuranceType) => {
      const insuranceFieldPrefix = getInsuranceFieldPrefix(insuranceType);
      const selectedTypeId = order?.[`${insuranceFieldPrefix}TypeId`];
      return Object.values(mainContext.insuranceSubTypesIndexed)
        .filter((subType) => subType.insuranceTypeId === selectedTypeId)
        .map((option) => ({
          title: option.name,
          value: option.id,
        }));
    };

    const selectedInsuranceSubType =
      chartContext.orderId && editMode
        ? getInsuranceSubTypeOptions("primary").find(
            (option) => option.value === order.insuranceSubTypeId,
          ) || null
        : null;
    const checkSubTypePayorId =
      chartContext.orderId && editMode
        ? mainContext.insuranceSubTypesIndexed[selectedInsuranceSubType?.value]
            ?.pvPayorId
        : null;

    const selectedSecondaryInsuranceSubType =
      chartContext.orderId && editMode
        ? getInsuranceSubTypeOptions("secondary").find(
            (option) => option.value === order.secondaryInsuranceSubTypeId,
          ) || null
        : null;
    const checkSecondarySubTypePayorId =
      chartContext.orderId && editMode
        ? mainContext.insuranceSubTypesIndexed[
            selectedSecondaryInsuranceSubType?.value
          ]?.pvPayorId
        : null;
    const getMbiError = (insuranceType, mbi) => {
      if (!chartContext.orderId) {
        return null;
      }
      let mbiError = null;
      const insuranceFieldPrefix = getInsuranceFieldPrefix(insuranceType);
      const isMedicare = order?.[`${insuranceFieldPrefix}TypeId`] === 1;
      const mbiToCheck =
        order?.[`${insuranceFieldPrefix}Policy`]?.toUpperCase();

      if (!isMedicare) {
        return null;
      } else if (!mbiToCheck || mbiToCheck.trim() === "") {
        return "MBI is required.";
      } else if (mbiToCheck.length !== 11) {
        return "MBI must be exactly 11 characters.";
      } else if (/[SLOIBZ]/.test(mbiToCheck)) {
        return "MBI can't contain S, L, O, I, B, or Z.";
      } else if (
        !/^[1-9][A-Z][0-9A-Z]\d[A-Z][0-9A-Z]\d[A-Z]{2}\d{2}$/.test(mbiToCheck)
      ) {
        return "Invalid MBI format.";
      }

      return mbiError;
    };

    const primaryMbiError = getMbiError(
      "primary",
      order?.insuranceTypeId === 1 ? order?.insurancePolicy : order?.mbi,
    );
    const secondaryMbiError = getMbiError(
      "secondary",
      order?.secondaryInsuranceTypeId === 1
        ? order?.secondaryInsurancePolicy
        : order?.mbi,
    );
    const primaryEligibilityDisableReason =
      getEligibilityDisableReason("primary");
    const secondaryEligibilityDisableReason =
      getEligibilityDisableReason("secondary");

    return (
      <MUIBox>
        {showVerifiedChip && (
          <MUIChip
            color={"primary"}
            icon={
              order?.insuranceVerified === true ? (
                <MUICheckCircleIcon />
              ) : (
                <MUICloseIcon />
              )
            }
            label={
              order?.insuranceVerified === true ? "Verified" : "Not Verified"
            }
            variant={
              order?.insuranceVerified === true ? "contained" : "outlined"
            }
            size="small"
            disabled={order?.insuranceVerified !== true}
            sx={{ mb: 2 }}
          />
        )}
        {renderInsuranceCannotBeUpdatedAlert()}
        <MUIBox>
          <MUIBox sx={{ display: "flex", alignItems: "center" }}>
            <MUIHealthAndSafetyIcon
              sx={{
                fontSize: "34px",
                color: (theme) => theme.palette.secondary.main,
                mr: 1,
              }}
            />
            <MUITypography variant="body1">Primary</MUITypography>
          </MUIBox>
          {renderEditInsuranceSection("primary")}

          {chartContext.orderId && editMode && showCheckEligibilityButton ? (
            <MUIGrid container spacing={1} rowSpacing={1} sx={{ mt: 1 }}>
              <MUIGrid
                size={{
                  lg: 3,
                  xs: 12,
                }}
              >
                <MUITooltip
                  title={primaryEligibilityDisableReason || primaryMbiError}
                >
                  <span>
                    <MUIButton
                      variant="contained"
                      color="primary"
                      loading={primaryInsuranceApiResultLoading}
                      startIcon={<MUISaveIcon />}
                      fullWidth
                      disabled={
                        (selectedInsuranceSubType && !checkSubTypePayorId) ||
                        primaryMbiError !== null ||
                        Boolean(getEligibilityDisableReason("primary")) ||
                        !dataSource?.insuranceTypeId ||
                        !dataSource?.insuranceSubTypeId ||
                        (dataSource?.insuranceTypeId !== 1 &&
                          !dataSource?.insurancePolicy)
                      }
                      onClick={() => {
                        if (chartContext.hasPendingChanges) {
                          chartContext.handleSaveChart(() => {
                            setKeepEditModeOpen(true);
                            handleCheckInsuranceEligibility(order, "primary");
                          });
                        } else {
                          handleCheckInsuranceEligibility(order, "primary");
                        }
                      }}
                    >
                      Check Eligibility
                    </MUIButton>
                  </span>
                </MUITooltip>
              </MUIGrid>
              {selectedInsuranceSubType &&
              !checkSubTypePayorId &&
              showEligibilitySummary ? (
                <MUIGrid
                  size={{
                    xs: 12,
                    lg: 9,
                  }}
                >
                  <MUIAlert severity="warning" sx={{ width: "100%" }}>
                    <MUITypography variant="subtitle2">
                      This payor is not configured for electronic eligibility.
                      Please send to Business Support.
                    </MUITypography>
                  </MUIAlert>
                </MUIGrid>
              ) : (
                <MUIGrid
                  size={{
                    xs: 12,
                    lg: 9,
                  }}
                >
                  {primaryInsuranceApiResultLoading ? (
                    <MUISkeleton variant="rectangular" height={60} />
                  ) : (
                    renderEligibilityResultsAlert("primary")
                  )}
                </MUIGrid>
              )}
            </MUIGrid>
          ) : null}
        </MUIBox>

        <MUIBox sx={{ mt: 4 }}>
          <MUIBox sx={{ display: "flex", alignItems: "center" }}>
            <MUIHealthAndSafetyIcon
              sx={{
                fontSize: "34px",
                color: (theme) => theme.palette.secondary.main,
                mr: 1,
              }}
            />
            <MUITypography variant="body1" sx={{ mr: 1 }}>
              Secondary
            </MUITypography>
            <MUIFormControlLabel
              control={
                <MUISwitch
                  size="small"
                  onChange={handleSecondaryInsuranceCheckboxChange}
                  checked={Boolean(hasSecondaryInsurance)}
                  disabled={
                    chartContext.orderId &&
                    order?.isApproved === true &&
                    allowDisablingFields
                  }
                />
              }
              labelPlacement="end"
              sx={{ m: 0 }}
            />
          </MUIBox>
          {hasSecondaryInsurance && renderEditInsuranceSection("secondary")}

          {hasSecondaryInsurance &&
          chartContext.orderId &&
          editMode &&
          showCheckEligibilityButton ? (
            <MUIGrid container spacing={1} rowSpacing={1} sx={{ mt: 1 }}>
              <MUIGrid
                size={{
                  lg: 3,
                  xs: 12,
                }}
              >
                <MUITooltip
                  title={secondaryEligibilityDisableReason || secondaryMbiError}
                >
                  <span>
                    <MUIButton
                      variant="contained"
                      color="primary"
                      loading={secondaryInsuranceApiResultLoading}
                      startIcon={<MUISaveIcon />}
                      fullWidth
                      disabled={
                        (selectedSecondaryInsuranceSubType &&
                          !checkSecondarySubTypePayorId) ||
                        secondaryMbiError !== null ||
                        Boolean(getEligibilityDisableReason("secondary")) ||
                        !dataSource?.secondaryInsuranceTypeId ||
                        !dataSource?.secondaryInsuranceSubTypeId ||
                        (dataSource?.secondaryInsuranceTypeId !== 1 &&
                          !dataSource?.secondaryInsurancePolicy)
                      }
                      onClick={() => {
                        if (chartContext.hasPendingChanges) {
                          chartContext.handleSaveChart(() => {
                            setKeepEditModeOpen(true);
                            handleCheckInsuranceEligibility(order, "secondary");
                          });
                        } else {
                          handleCheckInsuranceEligibility(order, "secondary");
                        }
                      }}
                    >
                      Check Eligibility
                    </MUIButton>
                  </span>
                </MUITooltip>
              </MUIGrid>
              {selectedSecondaryInsuranceSubType &&
              !checkSecondarySubTypePayorId &&
              showEligibilitySummary ? (
                <MUIGrid
                  size={{
                    xs: 12,
                    lg: 9,
                  }}
                >
                  <MUIAlert severity="warning" sx={{ width: "100%" }}>
                    <MUITypography variant="subtitle2">
                      This payor is not configured for electronic eligibility.
                      Please send to Business Support.
                    </MUITypography>
                  </MUIAlert>
                </MUIGrid>
              ) : (
                <MUIGrid
                  size={{
                    xs: 12,
                    lg: 9,
                  }}
                >
                  {secondaryInsuranceApiResultLoading ? (
                    <MUISkeleton variant="rectangular" height={60} />
                  ) : (
                    renderEligibilityResultsAlert("secondary")
                  )}
                </MUIGrid>
              )}
            </MUIGrid>
          ) : null}
          {renderOpenAndUnapprovedOrdersAlert()}
        </MUIBox>
      </MUIBox>
    );
  };

  /**
   * Renders the left column insurance read-only view.
   * @param {string} label - The label of the insurance.
   * @param {string} insuranceType - Insurance type ("primary" or "secondary").
   * @param {JSX.Element} [changesAlert] - Optional alert to display under the Insurance Type header.
   * @returns {JSX.Element} The left column insurance read-only view.
   */
  const renderLeftColumnInsuranceReadOnlyView = (
    label,
    insuranceType,
    changesAlert,
  ) => {
    const insuranceFieldPrefix = getInsuranceFieldPrefix(insuranceType);
    return (
      <MUIBox sx={insuranceType === "secondary" ? { mt: 3 } : {}}>
        <MUIBox sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <MUIHealthAndSafetyIcon
            sx={{
              fontSize: "34px",
              color: (theme) => theme.palette.secondary.main,
              mr: 1,
            }}
          />
          <MUITypography variant="body1">{label}</MUITypography>
        </MUIBox>
        {changesAlert}
        <MUIGrid container spacing={1}>
          <MUIGrid
            size={{
              lg: 4,
              xs: 12,
            }}
          >
            <InfoItem
              label={"Payor Source"}
              value={
                mainContext.insuranceTypesIndexed[
                  dataSource[`${insuranceFieldPrefix}TypeId`]
                ]?.name || "None"
              }
            />
          </MUIGrid>
          <MUIGrid
            size={{
              lg: 8,
              xs: 12,
            }}
          >
            <InfoItem
              label={"Insurance"}
              value={
                mainContext.insuranceSubTypesIndexed[
                  dataSource[`${insuranceFieldPrefix}SubTypeId`]
                ]?.name || "None"
              }
            />
          </MUIGrid>
          {dataSource[`${insuranceFieldPrefix}TypeId`] !== 4 && (
            <>
              <MUIGrid
                size={{
                  lg: 4,
                  xs: 12,
                }}
              >
                <InfoItem
                  label={
                    dataSource[`${insuranceFieldPrefix}TypeId`] === 1
                      ? "MBI"
                      : "Policy #"
                  }
                  value={dataSource[`${insuranceFieldPrefix}Policy`] || "None"}
                />
              </MUIGrid>
              <MUIGrid
                size={{
                  lg: 8,
                  xs: 12,
                }}
              >
                <InfoItem
                  label={"Group #"}
                  value={dataSource[`${insuranceFieldPrefix}Group`] || "None"}
                />
              </MUIGrid>
            </>
          )}
          {/* Show MBI if neither primary nor secondary insurance has insuranceTypeId === 1 */}
          {dataSource?.insuranceTypeId !== 1 &&
            dataSource?.secondaryInsuranceTypeId !== 1 &&
            insuranceType === "primary" && (
              <MUIGrid
                size={{
                  lg: 4,
                  xs: 12,
                }}
              >
                <InfoItem label={"MBI"} value={dataSource?.mbi || "None"} />
              </MUIGrid>
            )}
        </MUIGrid>
      </MUIBox>
    );
  };

  /**
   * Renders the right column insurance read-only view.
   * @param {string} label - The label of the insurance.
   * @param {string} insuranceType - Insurance type ("primary" or "secondary").
   * @param {JSX.Element} [changesAlert] - Optional alert to display under the Insurance Type header.
   * @returns {JSX.Element} The right column insurance read-only view.
   */
  const renderRightColumnInsuranceReadOnlyView = (
    label,
    insuranceType,
    changesAlert,
  ) => {
    const insuranceFieldPrefix = getInsuranceFieldPrefix(insuranceType);
    const getInsuranceSubTypeOptions = () => {
      const selectedTypeId = order?.[`${insuranceFieldPrefix}TypeId`];
      return Object.values(mainContext.insuranceSubTypesIndexed)
        .filter((subType) => subType.insuranceTypeId === selectedTypeId)
        .map((option) => ({
          title: option.name,
          value: option.id,
        }));
    };

    const selectedSubType =
      getInsuranceSubTypeOptions().find(
        (option) =>
          option.value === order?.[`${insuranceFieldPrefix}SubTypeId`],
      ) || null;
    const checkSubTypePayorId =
      mainContext.insuranceSubTypesIndexed[selectedSubType?.value]?.pvPayorId;

    return (
      <MUIBox sx={insuranceType === "secondary" ? { mt: 4 } : {}}>
        {insuranceType === "primary" && showVerifiedChip && (
          <MUIChip
            color={"primary"}
            icon={
              order?.insuranceVerified === true ? (
                <MUICheckCircleIcon />
              ) : (
                <MUICloseIcon />
              )
            }
            label={
              order?.insuranceVerified === true ? "Verified" : "Not Verified"
            }
            variant={
              order?.insuranceVerified === true ? "contained" : "outlined"
            }
            size="small"
            disabled={order?.insuranceVerified !== true}
            sx={{ mb: 2 }}
          />
        )}
        <MUIBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <MUIHealthAndSafetyIcon
            sx={{
              fontSize: "34px",
              color: (theme) => theme.palette.secondary.main,
              mr: 1,
            }}
          />
          <MUITypography variant="body1">{label}</MUITypography>
        </MUIBox>
        {changesAlert}
        <MUIGrid container spacing={1} rowSpacing={2}>
          <MUIGrid
            size={{
              lg: 9,
              xs: 12,
            }}
          >
            <MUIGrid
              container
              spacing={6}
              rowSpacing={2}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <MUIGrid
                size={{
                  lg: "auto",
                }}
              >
                <InfoItem
                  label={"Payor Source"}
                  value={
                    mainContext.insuranceTypesIndexed[
                      dataSource?.[`${insuranceFieldPrefix}TypeId`]
                    ]?.name || "None"
                  }
                />
              </MUIGrid>
              <MUIGrid
                size={{
                  lg: "auto",
                }}
              >
                <InfoItem
                  label={"Insurance"}
                  value={
                    mainContext.insuranceSubTypesIndexed[
                      dataSource?.[`${insuranceFieldPrefix}SubTypeId`]
                    ]?.name || "None"
                  }
                />
              </MUIGrid>
              {dataSource?.[`${insuranceFieldPrefix}TypeId`] !== 4 && (
                <>
                  <MUIGrid
                    size={{
                      lg: "auto",
                    }}
                  >
                    <InfoItem
                      label={
                        dataSource?.[`${insuranceFieldPrefix}TypeId`] === 1
                          ? "MBI"
                          : "Policy #"
                      }
                      value={
                        dataSource?.[`${insuranceFieldPrefix}Policy`] || "None"
                      }
                    />
                  </MUIGrid>
                  <MUIGrid
                    size={{
                      lg: "auto",
                    }}
                  >
                    <InfoItem
                      label={"Group #"}
                      value={
                        dataSource?.[`${insuranceFieldPrefix}Group`] || "None"
                      }
                    />
                  </MUIGrid>
                  {insuranceType === "primary" &&
                    dataSource?.insuranceTypeId !== 1 &&
                    dataSource?.secondaryInsuranceTypeId !== 1 &&
                    order?.mbi && (
                      <MUIGrid
                        size={{
                          lg: "auto",
                        }}
                      >
                        <InfoItem label={"MBI"} value={order.mbi} />
                      </MUIGrid>
                    )}
                </>
              )}
            </MUIGrid>
          </MUIGrid>
          {selectedSubType && !checkSubTypePayorId && showEligibilitySummary ? (
            <MUIGrid size={12}>
              <MUIAlert severity="warning" sx={{ mt: 1, width: "100%" }}>
                <MUITypography variant="subtitle2">
                  This payor is not configured for electronic eligibility.
                  Please send to Business Support.
                </MUITypography>
              </MUIAlert>
            </MUIGrid>
          ) : (
            <MUIGrid size={12}>
              {primaryInsuranceApiResultLoading ? (
                <MUISkeleton variant="rectangular" height={60} />
              ) : (
                renderEligibilityResultsAlert(insuranceType, true)
              )}
            </MUIGrid>
          )}
        </MUIGrid>
      </MUIBox>
    );
  };

  // Force in-network/out-network view based on eligibility results
  useEffect(() => {
    if (!showEligibilitySummary) {
      return;
    }

    const primaryEligibilityResults = getEligibility("primary");

    if (
      primaryEligibilityResults?.detail?.isProviderInNetwork === true &&
      !insuranceInNetworkView
    ) {
      setInsuranceInNetworkView(true);
    }
    if (
      primaryEligibilityResults?.detail?.isProviderInNetwork === false &&
      insuranceInNetworkView
    ) {
      setInsuranceInNetworkView(false);
    }

    const secondaryEligibilityResults = getEligibility("secondary");

    if (
      secondaryEligibilityResults?.detail?.isProviderInNetwork === true &&
      !secondaryInsuranceInNetworkView
    ) {
      setSecondaryInsuranceInNetworkView(true);
    }
    if (
      secondaryEligibilityResults?.detail?.isProviderInNetwork === false &&
      secondaryInsuranceInNetworkView
    ) {
      setSecondaryInsuranceInNetworkView(false);
    }
  }, []);

  /**
   * Checks the insurance eligibility for the given order and insurance type.
   * @param {Object} order - The order object.
   * @param {string} insuranceType - Insurance type ("primary" or "secondary").
   */
  const handleCheckInsuranceEligibility = (order, insuranceType) => {
    if (insuranceType === "primary") {
      setPrimaryInsuranceApiResultLoading(true);
    } else {
      setSecondaryInsuranceApiResultLoading(true);
    }
    let url = `/refreshPverifyCoverage/${order.id}`;
    if (insuranceType) {
      url += `?primarySecondaryInsurance=${encodeURIComponent(insuranceType)}`;
    }

    api
      .send("POST", url)
      .then((res) => {
        if (insuranceType === "primary") {
          setTransientPrimaryInsuranceApiResult(res);
          setPrimaryInsuranceDirty(false);
          setPrimaryInsuranceApiResultLoading(false);
        } else {
          setTransientSecondaryInsuranceApiResult(res);
          setSecondaryInsuranceDirty(false);
          setSecondaryInsuranceApiResultLoading(false);
        }
      })
      .catch((err) => {
        console.error(err);
        if (insuranceType === "primary") {
          setPrimaryInsuranceApiResultLoading(false);
        } else {
          setSecondaryInsuranceApiResultLoading(false);
        }
      });
  };

  // Get verified active insurances for eligibility summary text
  const getVerifiedActiveInsurances = (forSummaryText) => {
    if (!order || !showEligibilitySummary) {
      return [];
    }

    const primaryEligibilityResult = getEligibility("primary");
    const secondaryEligibilityResult = getEligibility("secondary");

    const primaryActive = primaryEligibilityResult?.eligible === true;
    const secondaryActive = secondaryEligibilityResult?.eligible === true;

    const shouldSet =
      forSummaryText ||
      (primaryActive && (!hasSecondaryInsurance || secondaryActive));

    if (!shouldSet) {
      return [];
    }

    const verifiedInsurances = [];

    if (primaryActive) {
      verifiedInsurances.push({
        type: "primary",
        results: primaryEligibilityResult,
      });
    }

    if (hasSecondaryInsurance && secondaryActive) {
      verifiedInsurances.push({
        type: "secondary",
        results: secondaryEligibilityResult,
      });
    }

    return verifiedInsurances;
  };

  const getCreateNoteBlockingMessage = () => {
    if (allowCreateNoteAlways || order.currentIntakeQuestionnaireId) {
      return null;
    }

    return "The intake questionnaire must be completed in order to mark as verified and add a note.";
  };

  /**
   * Resolves eligibility for a specific insurance type using a consistent source priority.
   *
   * Source priority:
   * 1. Current transient API response for that insurance type
   * 2. Stored API results collection
   *
   * `order` and all API sources are read from component scope.
   *
   * @param {"primary"|"secondary"} insuranceType - Which insurance eligibility to resolve.
   * @returns {Object|null} Parsed eligibility result or `null` when unavailable/not applicable.
   */
  const getEligibility = (insuranceType) => {
    if (insuranceType === "primary") {
      if (primaryInsuranceDirty) {
        return null;
      }
      return Eligibility.getPrimary(
        primaryInsuranceApiResults,
        order,
        transientPrimaryInsuranceApiResult,
      );
    }

    if (insuranceType === "secondary") {
      if (secondaryInsuranceDirty) {
        return null;
      }
      return Eligibility.getSecondary(
        secondaryInsuranceApiResults,
        order,
        transientSecondaryInsuranceApiResult,
      );
    }

    return null;
  };

  /**
   * Returns the reason eligibility should be disabled, or null when enabled.
   *
   * @param {string} insuranceType - Insurance type ("primary" or "secondary")
   * @returns {string|null}
   */
  const getEligibilityDisableReason = (insuranceType) => {
    // Eligibility checks only apply to order-level context.
    if (!chartContext.orderId || !order) {
      return null;
    }

    // Couple short-circuits that do not care about the last time
    // eligibility was run.

    // RTD can run eligibility as often as needed.
    if (order.status === 2) {
      return null;
    }

    // The manual override bypasses eligibility frequency and order workflow
    // restrictions.
    if (overrideEligibilityRestrictions) {
      return null;
    }

    // All other short-circuits generally do care about a few things.

    const verifiedInsurances = getVerifiedActiveInsurances(true);
    const verification = verifiedInsurances.find(
      (insurance) => insurance.type === insuranceType,
    )?.results;

    const verificationDate = verification
      ? moment(verification.detail.createDate)
      : null;
    const currentDate = moment();
    const alreadyRanThisCalendarMonth = Boolean(
      verificationDate &&
      verificationDate.month() === currentDate.month() &&
      verificationDate.year() === currentDate.year(),
    );

    if (alreadyRanThisCalendarMonth) {
      return "Eligibility has already been checked this month.";
    }

    // Service tickets are not restricted by workflow status.
    if (order.orderType === "Service") {
      return null;
    }

    // New (status 0), Internal Review (21), Approval (59), and
    // Service - Approval (68) can run eligibility.
    const isEligibilityWorkflowAllowed =
      order.status === 0 || [21, 59, 68].includes(order.orderStatusReasonId);

    if (isEligibilityWorkflowAllowed) {
      return null;
    }

    return "Eligibility cannot be run in this order status.";
  };

  /**
   * Renders an alert based on eligibility results.
   * Displays error description if present, otherwise shows plan name, status, and dates.
   *
   * @param {string} insuranceType - Insurance type ("primary" or "secondary")
   */
  const renderEligibilityResultsAlert = (insuranceType, isReadOnly) => {
    if (!showEligibilitySummary) {
      return;
    }

    const eligibilityResults = getEligibility(insuranceType);

    if (eligibilityResults) {
      if (eligibilityResults?.status === "unknown") {
        // Unknown = no results yet, don't show anything
        return null;
      } else if (eligibilityResults?.status === "error") {
        return (
          // <MUIGrid size={{ xs: 12, sm: 6, md: 9 }}>
          <>
            <MUIAlert severity="error" sx={{ mt: 1, width: "100%" }}>
              <MUITypography variant="subtitle2">
                {eligibilityResults?.detail?.errorDescription ||
                  "Error checking eligibility"}
              </MUITypography>
            </MUIAlert>
          </>
          // </MUIGrid>
        );
      } else {
        const additionalInfo = eligibilityResults?.detail?.additionalInfo;
        const exceptionNotes = eligibilityResults?.detail?.exceptionNotes;
        const payerInfo = Array.isArray(additionalInfo?.PAYER)
          ? additionalInfo.PAYER.join("\n")
          : "";
        const verificationInfo =
          Array.isArray(additionalInfo?.VERIFICATION) &&
          additionalInfo.VERIFICATION.length > 0
            ? additionalInfo.VERIFICATION.join("\n")
            : null;
        const billingInfo =
          Array.isArray(additionalInfo?.BILLING) &&
          additionalInfo.BILLING.length > 0
            ? additionalInfo.BILLING.join("\n")
            : null;
        const verificationInfoSeverity =
          verificationInfo && /send to verifications/i.test(verificationInfo)
            ? "warning"
            : "info";

        // Determine severity for main alert
        let severity = "info";
        if (
          eligibilityResults?.eligible === false ||
          eligibilityResults?.detail?.isProviderInNetwork === false
        ) {
          severity = "error";
        } else if (
          eligibilityResults?.eligible === true &&
          eligibilityResults?.detail?.isProviderInNetwork === true
        ) {
          severity = "success";
        } else if (
          eligibilityResults?.eligible === true &&
          eligibilityResults?.detail?.isProviderInNetwork == null
        ) {
          severity = "warning";
        }

        if (isReadOnly) {
          return (
            <MUIAlert
              severity={severity}
              sx={{
                mt: 1,
                width: "100%",
                display: "flex",
                alignItems: "center",
              }}
              action={
                <MUIButton
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={() => setEditMode(true)}
                  sx={{
                    whiteSpace: "nowrap",
                    mr: 2,
                  }}
                >
                  View Details
                </MUIButton>
              }
            >
              <>
                {eligibilityResults?.eligible === false ||
                eligibilityResults?.eligible === null ? (
                  <>
                    <MUITypography variant="subtitle2">
                      Status:{" "}
                      {eligibilityResults?.eligible === false
                        ? "Inactive"
                        : "Unknown"}
                    </MUITypography>
                    <MUITypography variant="subtitle2">
                      Date Checked:{" "}
                      {eligibilityResults?.detail?.createDate || "Unknown"}
                      {eligibilityResults?.detail?.isSkipped === true && (
                        <MUITooltip title="pVerify skipped check and returned previous result">
                          <MUIRedoIcon
                            fontSize="small"
                            sx={{ verticalAlign: "middle", ml: 1 }}
                          />
                        </MUITooltip>
                      )}
                    </MUITypography>
                  </>
                ) : (
                  <>
                    <MUIGrid
                      container
                      spacing={6}
                      sx={{ width: "100%", margin: 0 }}
                    >
                      <MUIGrid
                        sx={{
                          paddingLeft: "0 !important",
                          paddingTop: "0 !important",
                        }}
                        size="auto"
                      >
                        <MUITypography variant="subtitle2">
                          Status: Active
                        </MUITypography>
                        <MUITypography variant="subtitle2">
                          Date Checked:{" "}
                          {eligibilityResults?.detail?.createDate || "Unknown"}
                          {eligibilityResults?.detail?.isSkipped === true && (
                            <MUITooltip title="pVerify skipped check and returned previous result">
                              <MUIRedoIcon
                                fontSize="small"
                                sx={{ verticalAlign: "middle", ml: 1 }}
                              />
                            </MUITooltip>
                          )}
                        </MUITypography>
                        {eligibilityResults?.detail?.isProviderInNetwork ===
                        true ? (
                          <MUITypography variant="subtitle2">
                            In Network
                          </MUITypography>
                        ) : eligibilityResults?.detail?.isProviderInNetwork ===
                          false ? (
                          <MUITypography variant="subtitle2">
                            Out of Network
                          </MUITypography>
                        ) : (
                          <MUITypography variant="subtitle2">
                            Network Unknown
                          </MUITypography>
                        )}
                      </MUIGrid>
                    </MUIGrid>
                  </>
                )}
              </>
            </MUIAlert>
          );
        }
        return (
          <>
            <MUIAlert
              severity={severity}
              sx={{
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <MUIBox>
                {eligibilityResults?.eligible === false ||
                eligibilityResults?.eligible === null ? (
                  <>
                    <MUITypography variant="subtitle2">
                      Status:{" "}
                      {eligibilityResults?.eligible === false
                        ? "Inactive"
                        : "Unknown"}
                    </MUITypography>
                    <MUITypography variant="subtitle2">
                      Date Checked:{" "}
                      {eligibilityResults?.detail?.createDate || "Unknown"}
                      {eligibilityResults?.detail?.isSkipped === true && (
                        <MUITooltip title="pVerify skipped check and returned previous result">
                          <MUIRedoIcon
                            fontSize="small"
                            sx={{ verticalAlign: "middle", ml: 1 }}
                          />
                        </MUITooltip>
                      )}
                    </MUITypography>
                    {payerInfo ? (
                      <>
                        <MUIDivider sx={{ my: 1 }} />
                        <MUITypography variant="body2">
                          {payerInfo}
                        </MUITypography>
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    <MUITooltip
                      title={eligibilityResults?.detail?.planName || "N/A"}
                    >
                      <MUIAlertTitle>
                        Plan:
                        {(eligibilityResults?.detail?.planName || "").length >
                        100
                          ? `${(eligibilityResults?.detail?.planName || "").slice(0, 100)}...`
                          : eligibilityResults?.detail?.planName || "N/A"}
                      </MUIAlertTitle>
                    </MUITooltip>
                    {eligibilityResults?.detail?.payerName &&
                    !eligibilityResults?.detail?.isMedicare ? (
                      <>
                        <MUITypography variant="subtitle2">
                          Payer: {eligibilityResults?.detail?.payerName}
                        </MUITypography>
                      </>
                    ) : null}
                    {eligibilityResults?.detail?.policyType ? (
                      <>
                        <MUITypography variant="subtitle2">
                          Type: {eligibilityResults.detail.policyType}
                        </MUITypography>
                      </>
                    ) : null}
                    <MUIDivider sx={{ my: 1 }} />
                    <MUIGrid
                      container
                      spacing={6}
                      sx={{ width: "100%", margin: 0 }}
                    >
                      <MUIGrid
                        sx={{
                          paddingLeft: "0 !important",
                          paddingTop: "0 !important",
                        }}
                        size={6}
                      >
                        <MUITypography variant="subtitle2">
                          Status: Active
                        </MUITypography>
                        <MUITypography variant="subtitle2">
                          Date Checked:{" "}
                          {eligibilityResults?.detail?.createDate || "Unknown"}
                          {eligibilityResults?.detail?.isSkipped === true && (
                            <MUITooltip title="pVerify skipped check and returned previous result">
                              <MUIRedoIcon
                                fontSize="small"
                                sx={{ verticalAlign: "middle", ml: 1 }}
                              />
                            </MUITooltip>
                          )}
                        </MUITypography>
                      </MUIGrid>
                      <MUIGrid sx={{ paddingTop: "0 !important" }} size={6}>
                        <MUITypography variant="subtitle2">
                          {`${eligibilityResults?.detail?.isMedicare ? "Part B Deductible Remaining" : "Deductible Remaining"}: ${
                            eligibilityResults?.detail?.isMedicare
                              ? eligibilityResults?.detail?.deductible
                                  ?.partB === null
                                ? "N/A"
                                : new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  }).format(
                                    eligibilityResults?.detail?.deductible
                                      ?.partB,
                                  )
                              : insuranceInNetworkView
                                ? eligibilityResults?.detail?.deductible
                                    ?.inNetwork === null
                                  ? "N/A"
                                  : new Intl.NumberFormat("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                    }).format(
                                      eligibilityResults?.detail?.deductible
                                        ?.inNetwork,
                                    )
                                : eligibilityResults?.detail?.deductible
                                      ?.outNetwork === null
                                  ? "N/A"
                                  : new Intl.NumberFormat("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                    }).format(
                                      eligibilityResults?.detail?.deductible
                                        ?.outNetwork,
                                    )
                          }`}
                        </MUITypography>
                        <MUITypography variant="subtitle2">
                          Coinsurance:{" "}
                          {insuranceInNetworkView
                            ? eligibilityResults?.detail?.dmeSummary
                                ?.coInsuranceInNetwork || "N/A"
                            : eligibilityResults?.detail?.dmeSummary
                                ?.coInsuranceOutNetwork || "N/A"}
                        </MUITypography>
                        <MUITypography variant="subtitle2">
                          OOP Remaining:{" "}
                          {insuranceInNetworkView
                            ? eligibilityResults?.detail?.outOfPocket
                                ?.inNetwork === null
                              ? "N/A"
                              : new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(
                                  eligibilityResults?.detail?.outOfPocket
                                    ?.inNetwork,
                                )
                            : eligibilityResults?.detail?.outOfPocket
                                  ?.outNetwork === null
                              ? "N/A"
                              : new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(
                                  eligibilityResults?.detail?.outOfPocket
                                    ?.outNetwork,
                                )}
                        </MUITypography>
                      </MUIGrid>
                    </MUIGrid>
                    <MUIGrid
                      container
                      spacing={1}
                      sx={{ width: "100%", margin: 0 }}
                    >
                      <MUITypography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 1,
                          fontStyle: "italic",
                        }}
                      >
                        {(() => {
                          if (
                            eligibilityResults?.detail?.isProviderInNetwork ===
                            true
                          ) {
                            // Only display text, do not setState here
                            return "In Network • Showing In Network Benefits";
                          }
                          if (
                            eligibilityResults?.detail?.isProviderInNetwork ===
                            false
                          ) {
                            // Only display text, do not setState here
                            return "Out of Network • Showing Out of Network Benefits";
                          }

                          // Unknown network status, keep switch
                          return (
                            <>
                              Network Status Unknown
                              {` • Showing ${insuranceInNetworkView ? "In Network" : "Out of Network"} Benefits (`}
                              <MUITypography
                                component="span"
                                variant="body2"
                                sx={{
                                  cursor: "pointer",
                                  color: (theme) => theme.palette.info.light,
                                  textDecoration: "underline",
                                }}
                                onClick={() =>
                                  insuranceType === "primary"
                                    ? setInsuranceInNetworkView(
                                        !insuranceInNetworkView,
                                      )
                                    : setSecondaryInsuranceInNetworkView(
                                        !secondaryInsuranceInNetworkView,
                                      )
                                }
                              >
                                Switch
                              </MUITypography>
                              {`)`}
                            </>
                          );
                        })()}
                      </MUITypography>
                    </MUIGrid>
                    {/* Additional Info Section */}
                    {payerInfo ? (
                      <>
                        <MUIDivider sx={{ my: 1 }} />
                        <MUITypography variant="body2">
                          {payerInfo}
                        </MUITypography>
                      </>
                    ) : null}
                    {/* Exception Notes Section */}
                    {exceptionNotes ? (
                      <>
                        <MUIDivider sx={{ my: 1 }} />
                        <MUITypography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          Exception Notes:
                        </MUITypography>
                        <MUITypography variant="body2">
                          {exceptionNotes}
                        </MUITypography>
                      </>
                    ) : null}
                  </>
                )}
              </MUIBox>
            </MUIAlert>
            {renderSubscriberInfoAlert(eligibilityResults, insuranceType)}
            {verificationInfo && (
              <MUIAlert
                severity={verificationInfoSeverity}
                sx={{ mt: 1, width: "100%" }}
              >
                <MUITypography variant="body2">
                  {verificationInfo}
                </MUITypography>
              </MUIAlert>
            )}
            {billingInfo && (
              <MUIAlert severity="info" sx={{ mt: 1, width: "100%" }}>
                <MUITypography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                  {billingInfo}
                </MUITypography>
              </MUIAlert>
            )}
          </>
        );
      }
    }
  };

  /**
   * Renders the same/sim verification component.
   * @returns {JSX.Element} The same/sim verification component.
   */
  const renderSameSimVerification = () => {
    if (order?.insuranceTypeId !== 1) {
      return null;
    }

    const handleCheckSameSim = async (order) => {
      if (!order) {
        return;
      }

      setSameSimVerificationLoading(true);

      await api
        .send("POST", "/refreshSameSim/" + order.id)
        .then((res) => {
          setSameSimVerificationLoading(false);
          setSameSimResults(res);
        })
        .catch((err) => {
          console.log(err);
          setSameSimVerificationLoading(false);
        });
    };

    const apiResults =
      sameSimResults?.length > 0 ? sameSimResults : sameSimApiResults;

    const result = Eligibility.getSameSim(apiResults);

    // Check if the order is in RTD status
    const isRTDStatus = order?.status === 2;

    // Check if there are any results
    const hasResults = result?.status != null;
    const hasValidApiResult =
      !overrideEligibilityRestrictions &&
      hasResults &&
      result.status !== "error" &&
      !isRTDStatus; // Disable if not in RTD status

    // Check if the create date is the same month as the current date
    const createDate = moment(result.detail?.createDate);
    const currentDate = moment();

    // Override button has not been clicekd (still set to false), has results (status is not error), is not in RTD status, AND has not been checked this calendar month
    const isSameMonth =
      !overrideEligibilityRestrictions &&
      !isRTDStatus &&
      result.status !== "error" &&
      createDate.month() === currentDate.month() &&
      createDate.year() === currentDate.year();

    // Determine which policy number to use for MBI validation
    const mbiToCheck =
      order?.insuranceTypeId === 1
        ? order?.insurancePolicy?.toUpperCase()
        : null;

    // Determine validation error/tooltip message
    let tooltipMessage = "";
    if (order?.insuranceTypeId !== 1) {
      tooltipMessage = "Same/Sim can only be checked if Medicare";
    } else if (hasResults && isSameMonth) {
      tooltipMessage = "Same/Sim already checked this month";
    } else if (mbiToCheck?.length !== 11) {
      tooltipMessage = "MBI must be exactly 11 characters.";
    } else if (/[SLOIBZ]/.test(mbiToCheck)) {
      tooltipMessage = "MBI can't contain S, L, O, I, B, or Z.";
    } else if (
      !/^[1-9][A-Z][0-9A-Z]\d[A-Z][0-9A-Z]\d[A-Z]{2}\d{2}$/.test(mbiToCheck)
    ) {
      tooltipMessage = "Invalid MBI format.";
    }

    // Disable button if there is a valid API result or the tooltip message is not empty
    const disableButton = hasValidApiResult || tooltipMessage !== "";

    let severity = null;
    let message = null;

    if (result.status === "pending") {
      severity = "info";

      const etaSeconds = result.detail?.expectedTimeInSeconds;
      let etaMessage = "Pending";
      if (typeof etaSeconds === "number") {
        if (etaSeconds < 60) {
          etaMessage = "Expected in under a minute";
        } else if (etaSeconds < 90) {
          etaMessage = "Expected in about a minute";
        } else if (etaSeconds < 150) {
          etaMessage = "Expected in about two minutes";
        } else if (etaSeconds < 3600) {
          etaMessage = `Expected in about ${Math.round(etaSeconds / 60)} minutes`;
        } else {
          etaMessage = "Expected in an hour or longer";
        }
      }

      message = etaMessage;
    } else if (result.status === "error") {
      severity = "error";
      message = result.detail?.errorMessage || "Error checking Same/Sim";
    } else if (result.status === "warning") {
      severity = "warning";
      const claims = result.detail?.claims || [];
      if (claims.length > 0) {
        const list = claims
          .map(
            (c) =>
              `${c.hcpcsCode} on ${moment(c.dosStart).format("MM/DD/YYYY")}`,
          )
          .join(", ");
        message = `Wheelchair claim on file in last 5 years (${list})`;
      } else {
        message = "Wheelchair claim on file in last 5 years";
      }
    } else if (result.status === "processed") {
      if (result.eligible === false) {
        severity = "error";
        message = "Same/Sim on file";
      } else if (result.eligible === true) {
        severity = "success";
        message = "No Same/Sim on file";
      }
    }

    return (
      <MUIBox sx={{ mt: 3 }}>
        <MUIGrid
          container
          spacing={2}
          wrap="nowrap"
          sx={{
            alignItems: "center",
            mb: 1,
          }}
        >
          <MUIGrid size={12}>
            <MUITypography variant="subtitle2">Same/Sim</MUITypography>
          </MUIGrid>
        </MUIGrid>
        <MUIGrid
          container
          spacing={1}
          sx={{
            alignItems: "top",
          }}
        >
          <MUIGrid
            size={{
              lg: 3,
              xs: 12,
            }}
          >
            <MUITooltip title={tooltipMessage}>
              <span>
                <MUIButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 1, whiteSpace: "nowrap" }}
                  onClick={() => {
                    chartContext.handleSaveChart(() => {
                      setKeepEditModeOpen(true);
                      handleCheckSameSim(order);
                    });
                  }}
                  loading={sameSimVerificationLoading}
                  disabled={disableButton}
                  startIcon={<MUISaveIcon />}
                >
                  Check Same/Sim
                </MUIButton>
              </span>
            </MUITooltip>
          </MUIGrid>
          <MUIGrid
            size={{
              xs: 12,
              lg: 9,
            }}
          >
            {sameSimVerificationLoading ? (
              <MUISkeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
            ) : (
              severity && (
                <MUIAlert severity={severity} sx={{ mt: 1, width: "100%" }}>
                  <MUITypography variant="subtitle2">{message}</MUITypography>
                </MUIAlert>
              )
            )}
          </MUIGrid>
        </MUIGrid>
      </MUIBox>
    );
  };

  const renderSNFVerification = () => {
    if (order?.insuranceTypeId !== 1) {
      return null;
    }

    const handleCheckSNF = async (order) => {
      if (!order) {
        return;
      }

      setSNFVerificationLoading(true);

      await api
        .send("POST", "/refreshSNF/" + order.id)
        .then((res) => {
          setSNFVerificationLoading(false);
          setSNFResults(res);
        })
        .catch((err) => {
          console.log(err);
          setSNFVerificationLoading(false);
        });
    };

    const apiResults = snfResults?.length > 0 ? snfResults : snfApiResults;

    const result = Eligibility.getSNF(apiResults);

    // Check if the order is in RTD status
    const isRTDStatus = order?.status === 2;

    // Check if there are any results
    const hasResults = result?.status != null && !isRTDStatus; // Disable if not in RTD status

    // Check if the create date is the same month as the current date
    const createDate = hasResults ? moment(result.detail?.createDate) : null;
    const currentDate = moment();

    // Override button has not been clicekd (still set to false), has results (status is not error), is not in RTD status, AND has not been checked this calendar month
    const isSameMonth =
      !overrideEligibilityRestrictions &&
      hasResults &&
      result.status !== "error" &&
      !isRTDStatus &&
      createDate &&
      createDate.month() === currentDate.month() &&
      createDate.year() === currentDate.year();

    // Check if there is a processed on file
    const hasProcessedOnFile =
      !overrideEligibilityRestrictions &&
      hasResults &&
      result.status === "success" &&
      !isRTDStatus; // Disable if not in RTD status

    let severity = "";
    let message = "";
    let tooltipMessage = "";

    if (hasProcessedOnFile) {
      // Disable if already processed on file
      tooltipMessage = "SNF has already been processed.";
    } else if (isSameMonth) {
      // Disable if already checked this month
      tooltipMessage = "SNF has already been checked this month.";
    } else if (!isRTDStatus) {
      // Disable if not in RTD status
      tooltipMessage = "Order must be in RTD status to check SNF.";
    }

    if (result.status === "pending") {
      severity = "info";
      message = "Request Submitted successfully for SNF.";
    } else if (result.status === "error") {
      severity = "error";
      message = result.detail?.errorMessage || "Error checking SNF";
    } else if (result.status === "success") {
      severity = "success";
      message = result.detail?.errorMessage;
    }

    return (
      <MUIBox sx={{ mt: 3 }}>
        <MUIGrid
          container
          spacing={2}
          wrap="nowrap"
          sx={{
            alignItems: "center",
            mb: 1,
          }}
        >
          <MUIGrid size={12}>
            <MUITypography variant="subtitle2">SNF</MUITypography>
          </MUIGrid>
        </MUIGrid>

        <MUIGrid
          container
          spacing={1}
          sx={{
            alignItems: "top",
          }}
        >
          <MUIGrid
            size={{
              lg: 3,
              xs: 12,
            }}
          >
            <MUITooltip
              title={!overrideEligibilityRestrictions ? tooltipMessage : ""}
            >
              <span>
                <MUIButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  loading={snfVerificationLoading}
                  sx={{ mt: 1, whiteSpace: "nowrap" }}
                  onClick={() => {
                    chartContext.handleSaveChart(() => {
                      setKeepEditModeOpen(true);
                      handleCheckSNF(order);
                    });
                  }}
                  disabled={
                    (!isRTDStatus && !overrideEligibilityRestrictions) ||
                    isSameMonth ||
                    hasProcessedOnFile
                  } // Disable if not in RTD status or already checked this month or already processed on file or not Medicare
                  startIcon={<MUISaveIcon />}
                >
                  Check SNF
                </MUIButton>
              </span>
            </MUITooltip>
          </MUIGrid>

          <MUIGrid
            size={{
              xs: 12,
              lg: 9,
            }}
          >
            {snfVerificationLoading ? (
              <MUISkeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
            ) : (
              <MUIAlert severity={severity} sx={{ mt: 1, width: "100%" }}>
                <MUITypography variant="subtitle2">{message}</MUITypography>
              </MUIAlert>
            )}
          </MUIGrid>
        </MUIGrid>
      </MUIBox>
    );
  };

  const renderVerificationNoteTextfield = () => {
    const handleVerificationNoteChange = (updatedFields) => {
      setInsuranceVerificationNoteText(updatedFields.verificationNoteText);
    };

    const verifiedInsurances = getVerifiedActiveInsurances(true);
    const createNoteBlockingMessage = getCreateNoteBlockingMessage();

    if (createNoteBlockingMessage) {
      return (
        <MUIAlert severity="warning" sx={{ width: "100%" }}>
          {createNoteBlockingMessage}
        </MUIAlert>
      );
    }

    return (
      <MUIGrid container spacing={2}>
        <MUIGrid size={12}>
          <MUITextField
            autoFocus={true}
            multiline
            rows={9}
            fullWidth={true}
            label={"Verification Note"}
            value={insuranceVerificationNoteText || ""}
            onChange={(e) => {
              handleVerificationNoteChange({
                verificationNoteText: e.target.value,
              });
            }}
            variant="standard"
            slotProps={{
              htmlInput: { style: { resize: "vertical" } },
              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
        <MUIGrid size={12}>
          <MUIFormControl
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
            required={true}
            error={
              !!chartContext.validationIssues?.[className]?.orderActivity?.[
                chartContext.orderId
              ]?.insuranceVerified
            }
          >
            <MUITypography variant="subtitle1" sx={{ mr: 2 }}>
              Insurance Verified?
            </MUITypography>
            <MUIRadioGroup
              sx={{ mt: "5px" }}
              row
              onChange={(e) => {
                const isVerified = e.target.value === "yes";
                setInsuranceVerified(isVerified);
                setCardChart({
                  ...cardChart,
                  insuranceVerified: isVerified,
                  verifiedInsurances: verifiedInsurances,
                }); // Adding verified insurances so that Save button enables if the value of isVerified equals the same value as original chart. (It won't enable unless there is a diff)
                chartContext.onChange({
                  ["orderActivity"]: {
                    [chartContext.orderId]: {
                      insuranceVerified: isVerified,
                      verifiedInsurances: verifiedInsurances,
                    },
                  },
                });
              }}
            >
              <MUIFormControlLabel
                value="yes"
                control={<MUIRadio size="small" />}
                label="Yes"
              />
              <MUIFormControlLabel
                value="no"
                control={<MUIRadio size="small" />}
                label="No"
              />
            </MUIRadioGroup>
            {chartContext.validationIssues?.[className]?.orderActivity?.[
              chartContext.orderId
            ]?.insuranceVerified && (
              <MUIFormHelperText>
                {
                  chartContext.validationIssues[className].orderActivity[
                    chartContext.orderId
                  ].insuranceVerified
                }
              </MUIFormHelperText>
            )}
          </MUIFormControl>
        </MUIGrid>
      </MUIGrid>
    );
  };

  const renderOpenAndUnapprovedOrdersAlert = () => {
    const sortByCurOrder = (orders) => {
      const sorter = (a, b) => {
        if (a.id === chartContext.orderId && b.id !== chartContext.orderId) {
          return -1;
        }
        if (a.id !== chartContext.orderId && b.id === chartContext.orderId) {
          return 1;
        }
        return b["id"] - a["id"];
      };
      orders.sort(sorter);
      return orders;
    };

    const visibleOpenAndUnapprovedOrders = openAndUnapprovedOrders.filter(
      (order) => Number(order?.id) >= 0,
    );
    const sortedOpenAndUnapprovedOrders = sortByCurOrder(
      visibleOpenAndUnapprovedOrders,
    );

    if (
      sortedOpenAndUnapprovedOrders.length > 0 &&
      (!chartContext.orderId || order?.isApproved !== true) &&
      shouldSyncInsuranceChanges()
    ) {
      return (
        <MUIAlert severity="info" sx={{ mt: 2, width: "100%" }}>
          <MUITypography variant="subtitle2" sx={{ mb: 1 }}>
            Changes to the insurance will sync to the patient and the following
            unapproved tickets:
          </MUITypography>
          {sortedOpenAndUnapprovedOrders.map((order) => (
            <MUITypography
              variant="body2"
              key={order.id}
              sx={{
                color:
                  order.id === chartContext.orderId
                    ? (theme) => theme.palette.secondary.main
                    : null,
              }}
            >
              #{order.id} ({order.orderType}) -{" "}
              {
                mainContext.orderStatusReasonsIndexed[
                  order?.orderStatusReasonId
                ]?.description
              }
              {order.id === chartContext.orderId ? (
                <strong> (Current)</strong>
              ) : (
                ""
              )}
              <br />
            </MUITypography>
          ))}
        </MUIAlert>
      );
    }
  };

  const renderInsuranceCannotBeUpdatedAlert = () => {
    if (order?.isApproved === true) {
      return (
        <MUIAlert severity="warning" sx={{ mb: 2, width: "100%" }}>
          <MUITypography variant="subtitle2">
            This order has been approved and insurance cannot be updated.
          </MUITypography>
        </MUIAlert>
      );
    }
  };

  /**
   * Renders the read-only view displaying patient demographic information.
   * @returns {JSX.Element} Grid layout with patient information display
   */
  const renderReadOnlyView = () => {
    // Check if we're in insuranceDiscovery mode and if there are changes
    const showChangesAlert = chartContext.pageMode === "insuranceDiscovery";
    const primaryChanges = [];
    const secondaryChanges = [];

    if (showChangesAlert) {
      // Determine which original data source to compare against
      const originalDataSource =
        chartContext.orderId && order?.id > 0 ? originalOrder : originalPatient;

      const primaryFields = [
        {
          key: "insuranceTypeId",
          label: "Insurance Type",
          formatter: (val) =>
            mainContext.insuranceTypesIndexed[val]?.name || "[None]",
        },
        {
          key: "insuranceSubTypeId",
          label: "Payor",
          formatter: (val) =>
            mainContext.insuranceSubTypesIndexed[val]?.name || "[None]",
        },
        {
          key: "insurancePolicy",
          label: "Policy #",
          formatter: (val) => val || "[None]",
        },
        {
          key: "insuranceGroup",
          label: "Group #",
          formatter: (val) => val || "[None]",
        },
      ];

      const secondaryFields = [
        {
          key: "secondaryInsuranceTypeId",
          label: "Insurance Type",
          formatter: (val) =>
            mainContext.insuranceTypesIndexed[val]?.name || "[None]",
        },
        {
          key: "secondaryInsuranceSubTypeId",
          label: "Payor",
          formatter: (val) =>
            mainContext.insuranceSubTypesIndexed[val]?.name || "[None]",
        },
        {
          key: "secondaryInsurancePolicy",
          label: "Policy #",
          formatter: (val) => val || "[None]",
        },
        {
          key: "secondaryInsuranceGroup",
          label: "Group #",
          formatter: (val) => val || "[None]",
        },
      ];

      primaryFields.forEach((field) => {
        if (dataSource[field.key] !== originalDataSource[field.key]) {
          primaryChanges.push({
            label: field.label,
            before: field.formatter(originalDataSource[field.key]),
            after: field.formatter(dataSource[field.key]),
          });
        }
      });

      secondaryFields.forEach((field) => {
        if (dataSource[field.key] !== originalDataSource[field.key]) {
          secondaryChanges.push({
            label: field.label,
            before: field.formatter(originalDataSource[field.key]),
            after: field.formatter(dataSource[field.key]),
          });
        }
      });
    }

    const primaryChangesAlert =
      showChangesAlert && primaryChanges.length > 0 ? (
        <MUIAlert severity="info" sx={{ mb: 2, width: "100%" }}>
          <MUIAlertTitle>Primary Insurance Changes</MUIAlertTitle>
          <MUIBox component="ul" sx={{ pl: 2, mb: 0 }}>
            {primaryChanges.map((change, index) => (
              <MUITypography component="li" variant="body2" key={index}>
                <strong>{change.label}:</strong> {change.before} →{" "}
                {change.after}
              </MUITypography>
            ))}
          </MUIBox>
        </MUIAlert>
      ) : null;

    const secondaryChangesAlert =
      showChangesAlert && secondaryChanges.length > 0 ? (
        <MUIAlert severity="info" sx={{ mb: 2, width: "100%" }}>
          <MUIAlertTitle>Secondary Insurance Changes</MUIAlertTitle>
          <MUIBox component="ul" sx={{ pl: 2, mb: 0 }}>
            {secondaryChanges.map((change, index) => (
              <MUITypography component="li" variant="body2" key={index}>
                <strong>{change.label}:</strong> {change.before} →{" "}
                {change.after}
              </MUITypography>
            ))}
          </MUIBox>
        </MUIAlert>
      ) : null;

    return (
      <MUIBox>
        {props.sidebar
          ? renderLeftColumnInsuranceReadOnlyView(
              "Primary",
              "primary",
              primaryChangesAlert,
            )
          : renderRightColumnInsuranceReadOnlyView(
              "Primary",
              "primary",
              primaryChangesAlert,
            )}
        {hasSecondaryInsurance
          ? props.sidebar
            ? renderLeftColumnInsuranceReadOnlyView(
                "Secondary",
                "secondary",
                secondaryChangesAlert,
              )
            : renderRightColumnInsuranceReadOnlyView(
                "Secondary",
                "secondary",
                secondaryChangesAlert,
              )
          : null}
        {showChangesAlert &&
          (primaryChanges.length > 0 || secondaryChanges.length > 0) &&
          renderOpenAndUnapprovedOrdersAlert()}
      </MUIBox>
    );
  };

  /**
   * Renders the content of the card component.
   * @returns {JSX.Element} Grid layout with patient insurance information display
   */
  const renderContent = () => {
    if (editMode) {
      return (
        <>
          {renderEditView()}
          {/* {chartContext.orderId && showCheckSameSimButton ? renderSameSimVerification() : null} */}
          {/* {chartContext.orderId && showCheckSNFButton ? renderSNFVerification() : null} */}
        </>
      );
    }
    if (showCreateVerificationNoteButton && addVerificationNote) {
      return <>{renderVerificationNoteTextfield()}</>;
    }
    return renderReadOnlyView();
  };

  /**
   * Returns the action buttons for the card component.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const handleCancel = () => {
      const updatedInsurance = { ...dataSource };
      const updatedPatient = { ...patient };
      const updatedInsuranceOnAllOpenAndUnapprovedOrders = {
        ...openAndUnapprovedOrders,
      };
      const updatedChart = { ...chartContext.chart };
      const insuranceFields = cardChart ? Object.keys(cardChart) : [];

      const originalInsurance =
        chartContext.orderId && order?.id > 0 ? originalOrder : originalPatient;
      const originalInsuranceOnAllOpenAndUnapprovedOrders = Object.values(
        chartContext.originalChart.orderActivity,
      ).filter(
        (order) =>
          order?.isApproved !== true &&
          order.status !== 4 &&
          order.status !== 3,
      ); // 3 = Setup, 4 = Cancelled

      for (const field of insuranceFields) {
        if (dataSource[field] !== originalInsurance[field]) {
          updatedInsurance[field] = originalInsurance[field];
          chartContext.onChange({
            [chartContext?.orderId ? "orderActivity" : "patient"]: {
              [dataSource.id]: { ...updatedInsurance },
            },
          });
        }

        if (newNoteId) {
          delete updatedChart.note[newNoteId];
          chartContext.onChange({ note: { ...updatedChart.note } });
        }

        if (shouldSyncInsuranceChanges()) {
          if (patient?.[field] !== originalPatient?.[field]) {
            updatedPatient[field] = originalPatient[field];
          }
          for (const originalOrder of originalInsuranceOnAllOpenAndUnapprovedOrders) {
            const matchingOrder = Object.values(
              updatedInsuranceOnAllOpenAndUnapprovedOrders,
            ).find((o) => o.id === originalOrder.id);
            if (originalOrder[field] !== matchingOrder[field]) {
              matchingOrder[field] = originalOrder[field];
            }
            chartContext.onChange({
              orderActivity: { [matchingOrder.id]: { ...matchingOrder } },
            });
          }
          chartContext.onChange({
            patient: { [chartContext.patientId]: { ...updatedPatient } },
          });
        }
      }

      // Check if secondary insurance exists and set state for secondary insurance switch accordingly
      setHasSecondaryInsurance(
        Boolean(
          updatedInsurance.secondaryInsuranceTypeId ||
          updatedInsurance.secondaryInsuranceSubTypeId,
        ),
      );

      chartContext.onChange({
        [chartContext?.orderId ? "orderActivity" : "patient"]: {
          [dataSource.id]: { ...updatedInsurance },
        },
      });
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);
      setOverrideEligibilityRestrictions(false);
      setAddVerificationNote(false);
      setInsuranceVerificationNoteText("");
      setAddVerificationNote(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
    };

    const getInsuranceDiscoveryMenuItem = () => (
      <MUIMenuItem
        onClick={() => {
          if (chartContext.orderId) {
            chartContext.handleNavigateChart(
              `/chart/${chartContext.patientId}/order/${chartContext.orderId}/insuranceDiscovery`,
            );
          } else {
            chartContext.handleNavigateChart(
              `/chart/${chartContext.patientId}/insuranceDiscovery`,
            );
          }
        }}
      >
        <MUIListItemIcon>
          <MUIAutoAwesomeIcon fontSize="small" />
        </MUIListItemIcon>
        Insurance Discovery
      </MUIMenuItem>
    );

    const getOverrideEligibilityRestrictionsMenuItem = () => (
      <MUIMenuItem
        onClick={() => {
          setOverrideEligibilityRestrictions(true);
        }}
      >
        <MUIListItemIcon>
          <MUIPolicyIcon fontSize="small" />
        </MUIListItemIcon>
        Override Eligibility Restrictions
      </MUIMenuItem>
    );

    const menuItems = [];
    if (
      editMode &&
      chartContext.orderId &&
      Security.hasPermission("order.allowEligibilityRestrictionOverride")
    ) {
      menuItems.push(getOverrideEligibilityRestrictionsMenuItem());
    }
    if (
      Security.hasPermission("patient.insuranceDiscovery") &&
      chartContext.pageMode !== "insuranceDiscovery"
    ) {
      menuItems.push(getInsuranceDiscoveryMenuItem());
    }

    return [
      ...(editMode || addVerificationNote
        ? [
            <MUIButton
              color="error"
              disabled={chartContext.isUpdating}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </MUIButton>,
            ...(menuItems.length > 0 ? [<Menu menuItems={menuItems} />] : []),
          ]
        : [
            ...(chartContext.orderId && showCreateVerificationNoteButton
              ? [
                  <MUIButton
                    variant="outlined"
                    disabled={chartContext.isUpdating}
                    onClick={() => {
                      setAddVerificationNote(true);
                    }}
                    startIcon={<MUIAddIcon />}
                    size="small"
                  >
                    Create Note
                  </MUIButton>,
                ]
              : []),
            ...(showEditButton
              ? [
                  <MUIButton
                    disabled={chartContext.isUpdating}
                    onClick={() => setEditMode(true)}
                    size="small"
                  >
                    Edit
                  </MUIButton>,
                ]
              : []),
            ...(menuItems.length > 0 ? [<Menu menuItems={menuItems} />] : []),
          ]),
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartInsurance.meta = { className: className, title: title, icon: icon };
